<template>
  <v-container fluid>
    <v-row v-if="!dialogOnly && !errorMessage">
      <v-col cols="12">
        <v-data-table
          :headers="headers"
          :items="items"
          :search="search"
          :options.sync="options"
          :server-items-length="totalItems"
          :loading="apiLoading"
          :loading-text="$t('wkt.admin.common.table.loading')"
          :no-data-text="$t('wkt.admin.common.table.no_data')"
          :no-results-text="$t('wkt.admin.common.table.no_results')"
          :footer-props="{
            'items-per-page-options': [20, 50, 100],
            'items-per-page-text': $t('wkt.admin.common.table.items_per_page'),
            'items-per-page-all-text': $t('wkt.admin.common.table.items_per_page_all')
          }"
          class="elevation-0"
          :fixed-header="true"
        >
          <template v-slot:top>
            <v-toolbar flat color="white" class="mb-3">
              <v-row align="center">
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="search"
                    :label="$t('wkt.admin.workflows.table.search')"
                    hide-details
                    clearable
                    @click:clear="searchTimeOut()"
                    @keyup="searchTimeOut()"
                  />
                </v-col>
                <v-col cols="12" md="8" class="text-md-right">
                  <v-btn
                    v-if="canWrite"
                    text
                    color="primary"
                    class="mr-2"
                    @click="openTemplateDialog"
                  >
                    {{ $t('wkt.admin.workflows.actions.from_template') }}
                  </v-btn>
                  <v-btn
                    v-if="canWrite"
                    color="primary"
                    depressed
                    @click="openNewWorkflow"
                  >
                    {{ $t('wkt.admin.workflows.actions.new') }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-toolbar>
          </template>

          <template v-slot:[`item`]="{ item }">
            <tr class="workflow-row" @click="openWorkflow(item)">
              <td>{{ item.creationDate ? formatLongDateTime(item.creationDate, true) : '--' }}</td>
              <td>{{ item.name }}</td>
              <td>{{ formatEventType(item.eventType) }}</td>
              <td>{{ item.enabled ? $t('wkt.admin.common.yes') : $t('wkt.admin.common.no') }}</td>
              <td>{{ item.nextRunAt ? formatLongDateTime(item.nextRunAt, true) : '--' }}</td>
              <td>{{ item.updateDate ? formatLongDateTime(item.updateDate, true) : formatLongDateTime(item.creationDate, true) }}</td>
            </tr>
          </template>

          <template v-slot:[`footer.page-text`]="props">
            {{ props.pageStart }} - {{ props.pageStop }} {{ $t('wkt.admin.paged-tables.pager.of') }} {{ props.itemsLength > props.pageStop ? `+${props.pageStop}` : props.pageStop }}
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <v-dialog v-model="templateDialog" max-width="860" persistent scrollable>
      <v-card>
        <v-toolbar flat>
          <v-toolbar-title>{{ $t('wkt.admin.workflows.templates.dialog_title') }}</v-toolbar-title>
          <v-spacer />
          <v-btn
            icon
            color="grey darken-3"
            :title="$t('wkt.admin.common.dialog-buttons.close')"
            @click="closeTemplateDialog"
          >
            <v-icon color="grey darken-3">close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-divider />

        <v-card-text
          style="min-height: 340px; max-height: calc(100vh - 180px); overflow-y: auto;"
          @wheel="onWheel"
        >
          <v-skeleton-loader
            v-if="templatesLoading"
            type="list-item-three-line, list-item-three-line, list-item-three-line"
          />

          <v-alert
            v-else-if="!workflowTemplates.length"
            dense
            outlined
            type="info"
          >
            {{ $t('wkt.admin.workflows.templates.empty') }}
          </v-alert>

          <v-row v-else>
            <v-col
              v-for="template in workflowTemplates"
              :key="template.code"
              cols="12"
              md="6"
            >
              <v-card outlined height="100%">
                <v-card-text>
                  <div class="text-overline primary--text mb-1">
                    {{ $t('wkt.admin.workflows.templates.example_chip') }}
                  </div>
                  <div class="text-h6 mb-1">{{ template.name }}</div>
                  <div class="grey--text text--darken-1 mb-3">
                    {{ template.description }}
                  </div>
                  <div class="text-body-2 mb-2">
                    <strong>{{ $t('wkt.admin.workflows.fields.event_type') }}:</strong>
                    {{ formatEventType(template.eventType) }}
                  </div>
                  <div class="d-flex flex-wrap">
                    <v-chip
                      v-for="actionType in template.actionTypes || []"
                      :key="`${template.code}-${actionType}`"
                      small
                      class="mr-2 mb-2"
                    >
                      {{ formatActionType(actionType) }}
                    </v-chip>
                  </div>
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    color="primary"
                    text
                    :loading="createFromTemplateLoading === template.code"
                    @click="createWorkflowFromTemplate(template.code)"
                  >
                    {{ $t('wkt.admin.workflows.actions.use_template') }}
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="detailDialog" max-width="1180" persistent scrollable>
      <v-card>
        <v-toolbar flat>
          <v-toolbar-title>
            {{ workflowForm.id ? $t('wkt.admin.workflows.dialog.edit_title') : $t('wkt.admin.workflows.dialog.create_title') }}
          </v-toolbar-title>
          <v-spacer />
          <v-btn
            icon
            color="grey darken-3"
            :title="$t('wkt.admin.common.dialog-buttons.close')"
            @click="closeDetailDialog"
          >
            <v-icon color="grey darken-3">close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-divider />

        <v-card-text style="min-height: 540px;" @wheel="onWheel">
          <v-skeleton-loader
            v-if="detailLoading"
            type="article, article, article"
          />

          <v-form v-else ref="workflowFormRef" lazy-validation>
            <v-tabs
              background-color="transparent"
              class="mb-5 workflow-editor-tabs"
              grow
              hide-slider
            >
              <v-tab
                :class="editorStep === '1' ? 'primary--text font-weight-medium' : 'grey--text text--darken-1'"
                @click="editorStep = '1'"
              >
                {{ $t('wkt.admin.workflows.steps.base') }}
              </v-tab>
              <v-tab
                :class="editorStep === '2' ? 'primary--text font-weight-medium' : 'grey--text text--darken-1'"
                @click="editorStep = '2'"
              >
                {{ $t('wkt.admin.workflows.steps.conditions') }}
              </v-tab>
              <v-tab
                :class="editorStep === '3' ? 'primary--text font-weight-medium' : 'grey--text text--darken-1'"
                @click="editorStep = '3'"
              >
                {{ $t('wkt.admin.workflows.steps.actions') }}
              </v-tab>
              <v-tab
                v-if="workflowForm.id"
                :class="editorStep === '4' ? 'primary--text font-weight-medium' : 'grey--text text--darken-1'"
                @click="editorStep = '4'"
              >
                {{ $t('wkt.admin.workflows.sections.executions') }}
              </v-tab>
            </v-tabs>

            <v-tabs-items v-model="editorStep" class="workflow-editor-tab-items">
                <v-tab-item value="1" class="px-0">
                  <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="workflowForm.name"
                  :label="$t('wkt.admin.workflows.fields.name')"
                  :rules="[validationRules.required]"
                  :readonly="!canWrite"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-select
                  v-model="workflowForm.eventType"
                  :items="eventTypeItems"
                  item-text="text"
                  item-value="value"
                  :label="$t('wkt.admin.workflows.fields.event_type')"
                  :rules="[validationRules.required]"
                  :readonly="!canWrite"
                  :menu-props="getWorkflowDialogMenuProps()"
                  @change="handleEventTypeChange"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-switch
                  v-model="workflowForm.enabled"
                  :label="$t('wkt.admin.workflows.fields.enabled')"
                  :readonly="!canWrite"
                />
              </v-col>
              <v-col v-if="isPeriodicEvent" cols="12" md="3">
                <v-select
                  v-model="periodicConfig.frequency"
                  :items="periodicFrequencyItems"
                  item-text="text"
                  item-value="value"
                  :label="$t('wkt.admin.workflows.fields.periodic_frequency')"
                  :readonly="!canWrite"
                  :menu-props="getWorkflowDialogMenuProps()"
                  @change="handlePeriodicConfigChange"
                />
              </v-col>
              <v-col v-if="isPeriodicEvent" cols="12" md="3">
                <v-menu
                  v-model="periodicTimeMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                  :content-class="getWorkflowDialogMenuProps().contentClass"
                  :disabled="!canWrite"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      :value="periodicConfig.time"
                      :label="$t('wkt.admin.workflows.fields.periodic_time')"
                      :hint="$t('wkt.admin.workflows.fields.periodic_time_hint')"
                      persistent-hint
                      readonly
                      :disabled="!canWrite"
                      v-on="on"
                    />
                  </template>
                  <v-time-picker
                    :value="periodicConfig.time"
                    format="24hr"
                    scrollable
                    @input="updatePeriodicTime"
                  />
                </v-menu>
              </v-col>
              <v-col v-if="isPeriodicEvent && periodicConfig.frequency === 'WEEKLY'" cols="12" md="3">
                <v-select
                  v-model="periodicConfig.weekday"
                  :items="periodicWeekdayItems"
                  item-text="text"
                  item-value="value"
                  :label="$t('wkt.admin.workflows.fields.periodic_weekday')"
                  :readonly="!canWrite"
                  :menu-props="getWorkflowDialogMenuProps()"
                  @change="handlePeriodicConfigChange"
                />
              </v-col>
              <v-col v-if="isPeriodicEvent && periodicConfig.frequency === 'MONTHLY'" cols="12" md="3">
                <v-text-field
                  v-model="periodicConfig.dayOfMonth"
                  :label="$t('wkt.admin.workflows.fields.periodic_day_of_month')"
                  type="number"
                  min="1"
                  max="31"
                  :hint="$t('wkt.admin.workflows.fields.periodic_day_of_month_hint')"
                  persistent-hint
                  :readonly="!canWrite"
                  @input="handlePeriodicConfigChange"
                />
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="workflowForm.description"
                  :label="$t('wkt.admin.workflows.fields.description')"
                  auto-grow
                  rows="3"
                  :readonly="!canWrite"
                />
              </v-col>
            </v-row>
                </v-tab-item>

                <v-tab-item value="2" class="px-0">
            <v-alert
              v-if="isPeriodicEvent"
              dense
              outlined
              type="info"
              class="mb-4"
            >
              {{ $t('wkt.admin.workflows.empty.periodic_without_global_conditions') }}
            </v-alert>

            <v-alert
              v-if="!isPeriodicEvent && !workflowForm.conditions.length"
              dense
              outlined
              type="info"
              class="mb-4"
            >
              <div>{{ $t('wkt.admin.workflows.empty.conditions') }}</div>
            </v-alert>

            <v-expansion-panels
              v-if="!isPeriodicEvent && workflowForm.conditions.length"
              v-model="conditionEditorPanel"
              accordion
              flat
              class="mb-4"
            >
              <v-expansion-panel
                v-for="(condition, conditionIndex) in workflowForm.conditions"
                :key="condition.localId"
              >
                <v-expansion-panel-header>
                  <div class="d-flex flex-column">
                    <div class="text-subtitle-2 font-weight-medium">
                      {{ $t('wkt.admin.workflows.actions.condition_card_title', { index: conditionIndex + 1 }) }}
                    </div>
                    <div class="grey--text text--darken-1 text-body-2">
                      {{ buildConditionSummary(condition) }}
                    </div>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div class="d-flex align-center justify-end flex-wrap mb-3">
                    <div class="d-flex align-center flex-wrap">
                      <v-btn
                        v-if="canWrite"
                        icon
                        color="grey darken-2"
                        :title="$t('wkt.admin.workflows.actions.move_up')"
                        :disabled="conditionIndex === 0"
                        @click="moveCondition(conditionIndex, -1)"
                      >
                        <v-icon color="grey darken-2">arrow_upward</v-icon>
                      </v-btn>
                      <v-btn
                        v-if="canWrite"
                        icon
                        color="grey darken-2"
                        :title="$t('wkt.admin.workflows.actions.move_down')"
                        :disabled="conditionIndex === workflowForm.conditions.length - 1"
                        @click="moveCondition(conditionIndex, 1)"
                      >
                        <v-icon color="grey darken-2">arrow_downward</v-icon>
                      </v-btn>
                      <v-btn
                        v-if="canWrite"
                        icon
                        color="error"
                        :title="$t('wkt.admin.common.dialog-buttons.delete')"
                        @click="removeCondition(conditionIndex)"
                      >
                        <v-icon color="error">delete</v-icon>
                      </v-btn>
                    </div>
                  </div>
                  <v-row>
                    <v-col cols="12" md="4">
                      <v-select
                        v-model="condition.fieldName"
                        :items="conditionFieldItems"
                        item-text="text"
                        item-value="value"
                        :label="$t('wkt.admin.workflows.fields.condition_field')"
                        :readonly="!canWrite"
                        :menu-props="getWorkflowDialogMenuProps()"
                        @change="handleConditionFieldChange(condition)"
                      />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-select
                        v-model="condition.operator"
                        :items="getConditionOperatorItems(condition)"
                        item-text="text"
                        item-value="value"
                        :label="$t('wkt.admin.workflows.fields.operator')"
                        :readonly="!canWrite"
                        :menu-props="getWorkflowDialogMenuProps()"
                        @change="handleConditionOperatorChange(condition)"
                      />
                    </v-col>
                    <v-col cols="12" md="5">
                      <v-autocomplete
                        v-if="usesSelectableConditionValues(condition)"
                        v-model="condition.fieldValue"
                        :items="getConditionValueItems(condition)"
                        item-text="text"
                        item-value="value"
                        :label="getConditionValueLabel(condition)"
                        :multiple="isConditionMultiValue(condition)"
                        :chips="isConditionMultiValue(condition)"
                        :small-chips="isConditionMultiValue(condition)"
                        :deletable-chips="isConditionMultiValue(condition)"
                        :disabled="!canWrite"
                        :menu-props="getWorkflowDialogMenuProps()"
                        :no-data-text="$t('wkt.admin.common.table.no_data')"
                      />
                      <v-text-field
                        v-else-if="isConditionDateField(condition)"
                        v-model="condition.fieldValue"
                        type="date"
                        :label="getConditionValueLabel(condition)"
                        :readonly="!canWrite"
                      />
                      <v-text-field
                        v-else-if="isConditionTimeField(condition)"
                        v-model="condition.fieldValue"
                        type="time"
                        :label="getConditionValueLabel(condition)"
                        :readonly="!canWrite"
                      />
                      <v-combobox
                        v-else-if="isConditionMultiValue(condition)"
                        v-model="condition.fieldValue"
                        multiple
                        chips
                        small-chips
                        deletable-chips
                        :label="getConditionValueLabel(condition)"
                        :placeholder="getConditionValuePlaceholder(condition)"
                        :disabled="!canWrite"
                        :menu-props="getWorkflowDialogMenuProps()"
                      />
                      <v-text-field
                        v-else
                        v-model="condition.fieldValue"
                        :type="isConditionNumberField(condition) ? 'number' : 'text'"
                        :label="getConditionValueLabel(condition)"
                        :placeholder="getConditionValuePlaceholder(condition)"
                        :readonly="!canWrite"
                      />
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <div class="mb-6">
              <v-btn
                v-if="canWrite && !isPeriodicEvent"
                text
                color="primary"
                @click="addCondition"
              >
                {{ $t('wkt.admin.workflows.actions.add_condition') }}
              </v-btn>
            </div>
                </v-tab-item>

                <v-tab-item value="3" class="px-0">
            <v-alert
              v-if="!workflowForm.actions.length"
              dense
              outlined
              type="info"
            >
              <div>{{ $t('wkt.admin.workflows.empty.actions') }}</div>
            </v-alert>

            <v-expansion-panels
              v-if="workflowForm.actions.length"
              v-model="actionEditorPanel"
              accordion
              flat
              class="mb-4"
            >
              <v-expansion-panel
                v-for="(action, actionIndex) in workflowForm.actions"
                :key="action.localId"
              >
                <v-expansion-panel-header>
                  <div class="d-flex flex-column flex-grow-1 pr-4">
                    <div class="d-flex align-center flex-wrap mb-1">
                      <div class="text-subtitle-2 font-weight-medium mr-3">
                        {{ getActionCardTitle(actionIndex) }}
                      </div>
                      <v-chip small class="mr-2 mb-1" outlined>
                        {{ action.actionType || '--' }}
                      </v-chip>
                      <v-chip small class="mr-2 mb-1" outlined>
                        {{ action.recipientType || '--' }}
                      </v-chip>
                    </div>
                    <div class="grey--text text--darken-1 text-body-2">
                      {{ buildActionSummary(action) }}
                    </div>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                <div class="d-flex align-center justify-space-between flex-wrap mb-3">
                  <div class="d-flex align-center flex-wrap">
                    <v-chip small class="mr-2 mb-2" :color="getActionConditionCount(action) ? 'orange' : 'green'" outlined>
                      {{
                        getActionConditionCount(action)
                          ? $t('wkt.admin.workflows.actions.runs_when', { count: getActionConditionCount(action) })
                          : $t('wkt.admin.workflows.actions.runs_always')
                      }}
                    </v-chip>
                    <v-btn
                      v-if="canWrite"
                      icon
                      color="grey darken-2"
                      :title="$t('wkt.admin.workflows.actions.duplicate')"
                      @click="duplicateAction(actionIndex)"
                    >
                      <v-icon color="grey darken-2">content_copy</v-icon>
                    </v-btn>
                    <v-btn
                      v-if="canWrite"
                      icon
                      color="grey darken-2"
                      :title="$t('wkt.admin.workflows.actions.move_up')"
                      :disabled="actionIndex === 0"
                      @click="moveAction(actionIndex, -1)"
                    >
                      <v-icon color="grey darken-2">arrow_upward</v-icon>
                    </v-btn>
                    <v-btn
                      v-if="canWrite"
                      icon
                      color="grey darken-2"
                      :title="$t('wkt.admin.workflows.actions.move_down')"
                      :disabled="actionIndex === workflowForm.actions.length - 1"
                      @click="moveAction(actionIndex, 1)"
                    >
                      <v-icon color="grey darken-2">arrow_downward</v-icon>
                    </v-btn>
                    <v-btn
                      v-if="canWrite"
                      icon
                      color="error"
                      :title="$t('wkt.admin.common.dialog-buttons.delete')"
                      @click="removeAction(actionIndex)"
                    >
                      <v-icon color="error">delete</v-icon>
                    </v-btn>
                  </div>
                </div>

                <v-row>
                  <v-col cols="12" md="6">
                    <v-card
                      :key="`action-editor-${action.localId}-${action.actionType || 'empty'}`"
                      outlined
                      class="workflow-action-zone mb-4 mb-md-0"
                    >
                      <v-card-text>
                        <div class="text-subtitle-2 font-weight-medium mb-3">
                          {{ $t('wkt.admin.workflows.sections.what_it_does') }}
                        </div>
                        <v-row>
                          <v-col cols="12" md="7">
                            <v-select
                              v-model="action.actionType"
                              :items="getOrderedActionTypeItems()"
                              item-text="text"
                              item-value="value"
                              :label="$t('wkt.admin.workflows.fields.action_type')"
                              :readonly="!canWrite"
                              :menu-props="getWorkflowDialogMenuProps({ maxHeight: 260, closeOnContentClick: true })"
                              @input="handleActionTypeInput(action, $event)"
                            />
                          </v-col>
                          <v-col cols="12" md="5">
                            <v-text-field
                              :value="actionIndex + 1"
                              :label="$t('wkt.admin.workflows.fields.order')"
                              readonly
                            />
                          </v-col>
                        </v-row>

                    <div
                      v-if="getActionPresetItems(action).length"
                      class="mb-3"
                    >
                      <div class="caption grey--text text--darken-1 mb-2">
                        {{ $t('wkt.admin.workflows.sections.action_presets') }}
                      </div>
                      <div class="d-flex flex-wrap">
                        <v-chip
                          v-for="preset in getActionPresetItems(action)"
                          :key="`${action.localId}-preset-${preset.code}`"
                          x-small
                          class="mr-2 mb-2 workflow-clickable-chip"
                          outlined
                          :ripple="true"
                          @click="applyActionPreset(action, preset.code)"
                        >
                          {{ preset.label }}
                        </v-chip>
                      </div>
                    </div>

                    <v-text-field
                      v-if="action.actionType === 'CREATE_TASK'"
                      :id="getActionTemplateInputId(action, 'title')"
                      :value="getActionConfigValue(action, 'title')"
                      :label="$t('wkt.admin.workflows.fields.task_title')"
                      :readonly="!canWrite"
                      @focusin.native="registerActionTemplateTarget(action, 'title', $event)"
                      @click.native="registerActionTemplateTarget(action, 'title', $event)"
                      @keyup.native="registerActionTemplateTarget(action, 'title', $event)"
                      @input="updateActionConfigValue(action, 'title', $event)"
                    />
                    <v-textarea
                      v-if="action.actionType === 'CREATE_TASK'"
                      :id="getActionTemplateInputId(action, 'description')"
                      :value="getActionConfigValue(action, 'description')"
                      :label="$t('wkt.admin.workflows.fields.task_description')"
                      auto-grow
                      rows="3"
                      :readonly="!canWrite"
                      @focusin.native="registerActionTemplateTarget(action, 'description', $event)"
                      @click.native="registerActionTemplateTarget(action, 'description', $event)"
                      @keyup.native="registerActionTemplateTarget(action, 'description', $event)"
                      @input="updateActionConfigValue(action, 'description', $event)"
                    />
                    <v-text-field
                      v-if="action.actionType === 'CREATE_TASK'"
                      :value="getActionConfigValue(action, 'dueDays')"
                      :label="$t('wkt.admin.workflows.fields.task_due_days')"
                      type="number"
                      :readonly="!canWrite"
                      @input="updateNumericActionConfigValue(action, 'dueDays', $event)"
                    />

                    <v-text-field
                      v-if="action.actionType === 'SEND_EMAIL'"
                      :id="getActionTemplateInputId(action, 'subject')"
                      :value="getActionConfigValue(action, 'subject')"
                      :label="$t('wkt.admin.workflows.fields.email_subject')"
                      :readonly="!canWrite"
                      @focusin.native="registerActionTemplateTarget(action, 'subject', $event)"
                      @click.native="registerActionTemplateTarget(action, 'subject', $event)"
                      @keyup.native="registerActionTemplateTarget(action, 'subject', $event)"
                      @input="updateActionConfigValue(action, 'subject', $event)"
                    />
                    <v-textarea
                      v-if="action.actionType === 'SEND_EMAIL'"
                      :id="getActionTemplateInputId(action, 'body')"
                      :value="getActionConfigValue(action, 'body')"
                      :label="$t('wkt.admin.workflows.fields.email_body')"
                      auto-grow
                      rows="4"
                      :readonly="!canWrite"
                      @focusin.native="registerActionTemplateTarget(action, 'body', $event)"
                      @click.native="registerActionTemplateTarget(action, 'body', $event)"
                      @keyup.native="registerActionTemplateTarget(action, 'body', $event)"
                      @input="updateActionConfigValue(action, 'body', $event)"
                    />

                    <v-text-field
                      v-if="action.actionType === 'SEND_MESSAGE'"
                      :id="getActionTemplateInputId(action, 'title')"
                      :value="getActionConfigValue(action, 'title')"
                      :label="$t('wkt.admin.workflows.fields.message_title')"
                      :readonly="!canWrite"
                      @focusin.native="registerActionTemplateTarget(action, 'title', $event)"
                      @click.native="registerActionTemplateTarget(action, 'title', $event)"
                      @keyup.native="registerActionTemplateTarget(action, 'title', $event)"
                      @input="updateActionConfigValue(action, 'title', $event)"
                    />
                    <v-textarea
                      v-if="action.actionType === 'SEND_MESSAGE'"
                      :id="getActionTemplateInputId(action, 'body')"
                      :value="getActionConfigValue(action, 'body')"
                      :label="$t('wkt.admin.workflows.fields.message_body')"
                      auto-grow
                      rows="4"
                      :readonly="!canWrite"
                      @focusin.native="registerActionTemplateTarget(action, 'body', $event)"
                      @click.native="registerActionTemplateTarget(action, 'body', $event)"
                      @keyup.native="registerActionTemplateTarget(action, 'body', $event)"
                      @input="updateActionConfigValue(action, 'body', $event)"
                    />
                    <v-switch
                      v-if="action.actionType === 'SEND_MESSAGE'"
                      :input-value="!!getActionConfigValue(action, 'sticky')"
                      :label="$t('wkt.admin.workflows.fields.message_sticky')"
                      :readonly="!canWrite"
                      @change="updateBooleanActionConfigValue(action, 'sticky', $event)"
                    />

                    <v-text-field
                      v-if="action.actionType === 'REQUEST_DOCUMENT'"
                      :id="getActionTemplateInputId(action, 'requestTitle')"
                      :value="getActionConfigValue(action, 'requestTitle')"
                      :label="$t('wkt.admin.workflows.fields.request_document_title')"
                      :readonly="!canWrite"
                      @focusin.native="registerActionTemplateTarget(action, 'requestTitle', $event)"
                      @click.native="registerActionTemplateTarget(action, 'requestTitle', $event)"
                      @keyup.native="registerActionTemplateTarget(action, 'requestTitle', $event)"
                      @input="updateActionConfigValue(action, 'requestTitle', $event)"
                    />
                    <v-textarea
                      v-if="action.actionType === 'REQUEST_DOCUMENT'"
                      :id="getActionTemplateInputId(action, 'requestMessage')"
                      :value="getActionConfigValue(action, 'requestMessage')"
                      :label="$t('wkt.admin.workflows.fields.request_document_message')"
                      auto-grow
                      rows="4"
                      :readonly="!canWrite"
                      @focusin.native="registerActionTemplateTarget(action, 'requestMessage', $event)"
                      @click.native="registerActionTemplateTarget(action, 'requestMessage', $event)"
                      @keyup.native="registerActionTemplateTarget(action, 'requestMessage', $event)"
                      @input="updateActionConfigValue(action, 'requestMessage', $event)"
                    />
                    <v-switch
                      v-if="action.actionType === 'REQUEST_DOCUMENT'"
                      :input-value="getActionConfigBooleanValue(action, 'showDocument', true)"
                      :label="$t('wkt.admin.workflows.fields.request_document_show')"
                      :readonly="!canWrite"
                      @change="updateBooleanActionConfigValue(action, 'showDocument', $event)"
                    />
                    <v-switch
                      v-if="action.actionType === 'REQUEST_DOCUMENT'"
                      :input-value="getActionConfigBooleanValue(action, 'notifyEmployee', true)"
                      :label="$t('wkt.admin.workflows.fields.request_document_notify')"
                      :readonly="!canWrite"
                      @change="updateBooleanActionConfigValue(action, 'notifyEmployee', $event)"
                    />

                    <v-autocomplete
                      v-if="action.actionType === 'SHARE_DOCUMENT'"
                      :value="getActionConfigValue(action, 'sourceDocumentId')"
                      :items="workflowSourceDocuments"
                      item-value="id"
                      :item-text="sourceDocumentText"
                      :label="$t('wkt.admin.workflows.fields.share_document_source')"
                      :readonly="!canWrite"
                      :menu-props="getWorkflowDialogMenuProps({ maxHeight: 260, closeOnContentClick: true })"
                      clearable
                      @input="updateNumericActionConfigValue(action, 'sourceDocumentId', $event)"
                    />
                    <v-text-field
                      v-if="action.actionType === 'SHARE_DOCUMENT'"
                      :id="getActionTemplateInputId(action, 'requestTitle')"
                      :value="getActionConfigValue(action, 'requestTitle')"
                      :label="$t('wkt.admin.workflows.fields.share_document_title')"
                      :readonly="!canWrite"
                      @focusin.native="registerActionTemplateTarget(action, 'requestTitle', $event)"
                      @click.native="registerActionTemplateTarget(action, 'requestTitle', $event)"
                      @keyup.native="registerActionTemplateTarget(action, 'requestTitle', $event)"
                      @input="updateActionConfigValue(action, 'requestTitle', $event)"
                    />
                    <v-textarea
                      v-if="action.actionType === 'SHARE_DOCUMENT'"
                      :id="getActionTemplateInputId(action, 'requestMessage')"
                      :value="getActionConfigValue(action, 'requestMessage')"
                      :label="$t('wkt.admin.workflows.fields.share_document_message')"
                      auto-grow
                      rows="4"
                      :readonly="!canWrite"
                      @focusin.native="registerActionTemplateTarget(action, 'requestMessage', $event)"
                      @click.native="registerActionTemplateTarget(action, 'requestMessage', $event)"
                      @keyup.native="registerActionTemplateTarget(action, 'requestMessage', $event)"
                      @input="updateActionConfigValue(action, 'requestMessage', $event)"
                    />
                    <v-switch
                      v-if="action.actionType === 'SHARE_DOCUMENT'"
                      :input-value="getActionConfigBooleanValue(action, 'requestSignature', false)"
                      :label="$t('wkt.admin.workflows.fields.share_document_request_signature')"
                      :readonly="!canWrite"
                      @change="updateBooleanActionConfigValue(action, 'requestSignature', $event)"
                    />
                    <v-select
                      v-if="action.actionType === 'SHARE_DOCUMENT' && getActionConfigBooleanValue(action, 'requestSignature', false)"
                      :value="getActionConfigValue(action, 'signatureMode') || 'SMS'"
                      :items="signatureModeItems"
                      item-text="text"
                      item-value="value"
                      :label="$t('wkt.admin.workflows.fields.share_document_signature_mode')"
                      :readonly="!canWrite"
                      :menu-props="getWorkflowDialogMenuProps({ maxHeight: 260, closeOnContentClick: true })"
                      @input="updateActionConfigValue(action, 'signatureMode', $event)"
                    />

                    <v-autocomplete
                      v-if="action.actionType === 'SEND_FORM'"
                      :value="getActionConfigValue(action, 'formId')"
                      :items="workflowForms"
                      item-value="id"
                      :item-text="workflowFormText"
                      :label="$t('wkt.admin.workflows.fields.send_form_source')"
                      :readonly="!canWrite"
                      :menu-props="getWorkflowDialogMenuProps({ maxHeight: 260, closeOnContentClick: true })"
                      clearable
                      @input="updateNumericActionConfigValue(action, 'formId', $event)"
                    />
                    <v-text-field
                      v-if="action.actionType === 'SEND_FORM'"
                      :id="getActionTemplateInputId(action, 'requestTitle')"
                      :value="getActionConfigValue(action, 'requestTitle')"
                      :label="$t('wkt.admin.workflows.fields.send_form_title')"
                      :readonly="!canWrite"
                      @focusin.native="registerActionTemplateTarget(action, 'requestTitle', $event)"
                      @click.native="registerActionTemplateTarget(action, 'requestTitle', $event)"
                      @keyup.native="registerActionTemplateTarget(action, 'requestTitle', $event)"
                      @input="updateActionConfigValue(action, 'requestTitle', $event)"
                    />
                    <v-textarea
                      v-if="action.actionType === 'SEND_FORM'"
                      :id="getActionTemplateInputId(action, 'requestMessage')"
                      :value="getActionConfigValue(action, 'requestMessage')"
                      :label="$t('wkt.admin.workflows.fields.send_form_message')"
                      auto-grow
                      rows="4"
                      :readonly="!canWrite"
                      @focusin.native="registerActionTemplateTarget(action, 'requestMessage', $event)"
                      @click.native="registerActionTemplateTarget(action, 'requestMessage', $event)"
                      @keyup.native="registerActionTemplateTarget(action, 'requestMessage', $event)"
                      @input="updateActionConfigValue(action, 'requestMessage', $event)"
                    />

                    <div v-if="!isGuidedActionType(action.actionType)" class="grey--text text--darken-1 text-body-2 mb-3">
                      {{ $t('wkt.admin.workflows.fields.config_hint', { actionType: action && action.actionType ? action.actionType : '--' }) }}
                    </div>
                    <div
                      v-if="getMissingActionConfigKeys(action).length"
                      class="caption error--text mt-1"
                    >
                      {{
                        $t('wkt.admin.workflows.validation.missing_action_config_keys', {
                          index: actionIndex + 1,
                          keys: getMissingActionConfigKeys(action).join(', ')
                        })
                      }}
                    </div>

                    <div v-if="getActionTemplateVariableItems(action).length" class="mt-4">
                      <div class="caption grey--text text--darken-1 mb-2">
                        {{ $t('wkt.admin.workflows.sections.available_variables') }}
                      </div>
                      <div class="d-flex flex-wrap">
                        <v-chip
                          v-for="variable in getActionTemplateVariableItems(action)"
                          :key="`${action.localId}-variable-${variable.value}`"
                          x-small
                          class="mr-2 mb-2 workflow-clickable-chip"
                          outlined
                          :title="variable.text"
                          :ripple="true"
                          @click.stop="insertActionTemplateVariable(action, variable.token)"
                        >
                          {{ variable.token }}
                        </v-chip>
                      </div>
                    </div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-card outlined class="workflow-action-zone mb-4">
                      <v-card-text>
                        <div class="text-subtitle-2 font-weight-medium mb-3">
                          {{ $t('wkt.admin.workflows.sections.recipient') }}
                        </div>

                        <v-select
                          v-model="action.recipientType"
                          :items="getRecipientTypeItems(action)"
                          item-text="text"
                          item-value="value"
                          :label="$t('wkt.admin.workflows.fields.recipient_type')"
                          :readonly="!canWrite"
                          :menu-props="getWorkflowDialogMenuProps()"
                          @change="handleRecipientTypeChange(action)"
                        />

                        <v-select
                          v-if="action.recipientType === 'ADMIN'"
                          :value="getAdminRecipientPermissionModule(action)"
                          :items="getAdminRecipientPermissionModuleItems()"
                          item-text="text"
                          item-value="value"
                          :label="$t('wkt.admin.workflows.fields.admin_permission_module')"
                          :hint="$t('wkt.admin.workflows.fields.admin_permission_module_hint')"
                          persistent-hint
                          :readonly="!canWrite"
                          :menu-props="getWorkflowDialogMenuProps()"
                          @change="updateAdminRecipientPermissionModule(action, $event)"
                        />

                        <v-autocomplete
                          v-if="action.recipientType === 'EXPLICIT'"
                          :value="getExplicitRecipientEmployeeIds(action)"
                          :items="employees"
                          :item-text="employeeText"
                          item-value="id"
                          multiple
                          chips
                          deletable-chips
                          small-chips
                          clearable
                          :label="$t('wkt.admin.workflows.fields.explicit_employee_ids')"
                          :hint="$t('wkt.admin.workflows.fields.explicit_employee_ids_hint')"
                          persistent-hint
                          :menu-props="getWorkflowDialogMenuProps({ maxHeight: 320 })"
                          :readonly="!canWrite"
                          @change="updateExplicitRecipientEmployeeIds(action, $event)"
                        />
                      </v-card-text>
                    </v-card>

                    <v-card outlined class="workflow-action-zone mb-4">
                      <v-card-text>
                        <div class="text-subtitle-2 font-weight-medium mb-3">
                          {{ $t('wkt.admin.workflows.sections.conditions') }}
                        </div>

                        <v-alert
                          v-if="!getActionConditions(action).length"
                          dense
                          outlined
                          type="info"
                          class="mb-3"
                        >
                          {{ $t('wkt.admin.workflows.empty.action_conditions') }}
                        </v-alert>

                        <v-card
                          v-for="(condition, conditionIndex) in getActionConditions(action)"
                          :key="condition.localId"
                          outlined
                          class="mb-3"
                        >
                          <v-card-text>
                            <div class="d-flex align-center justify-space-between flex-wrap mb-3">
                              <div class="text-subtitle-2 font-weight-medium mr-4">
                                {{ $t('wkt.admin.workflows.actions.action_condition_card_title', { index: conditionIndex + 1 }) }}
                              </div>
                              <div class="d-flex align-center flex-wrap">
                                <v-btn
                                  v-if="canWrite"
                                  icon
                                  color="grey darken-2"
                                  :title="$t('wkt.admin.workflows.actions.move_up')"
                                  :disabled="conditionIndex === 0"
                                  @click="moveActionCondition(action, conditionIndex, -1)"
                                >
                                  <v-icon color="grey darken-2">arrow_upward</v-icon>
                                </v-btn>
                                <v-btn
                                  v-if="canWrite"
                                  icon
                                  color="grey darken-2"
                                  :title="$t('wkt.admin.workflows.actions.move_down')"
                                  :disabled="conditionIndex === getActionConditions(action).length - 1"
                                  @click="moveActionCondition(action, conditionIndex, 1)"
                                >
                                  <v-icon color="grey darken-2">arrow_downward</v-icon>
                                </v-btn>
                                <v-btn
                                  v-if="canWrite"
                                  icon
                                  color="error"
                                  :title="$t('wkt.admin.common.dialog-buttons.delete')"
                                  @click="removeActionCondition(action, conditionIndex)"
                                >
                                  <v-icon color="error">delete</v-icon>
                                </v-btn>
                              </div>
                            </div>

                            <v-row>
                              <v-col cols="12" md="4">
                                <v-select
                                  v-model="condition.fieldName"
                                  :items="conditionFieldItems"
                                  item-text="text"
                                  item-value="value"
                                  :label="$t('wkt.admin.workflows.fields.condition_field')"
                                  :readonly="!canWrite"
                                  :menu-props="getWorkflowDialogMenuProps()"
                                  @change="handleConditionFieldChange(condition)"
                                />
                              </v-col>
                              <v-col cols="12" md="3">
                                <v-select
                                  v-model="condition.operator"
                                  :items="getConditionOperatorItems(condition)"
                                  item-text="text"
                                  item-value="value"
                                  :label="$t('wkt.admin.workflows.fields.operator')"
                                  :readonly="!canWrite"
                                  :menu-props="getWorkflowDialogMenuProps()"
                                  @change="handleConditionOperatorChange(condition)"
                                />
                              </v-col>
                              <v-col cols="12" md="5">
                                <v-autocomplete
                                  v-if="usesSelectableConditionValues(condition)"
                                  v-model="condition.fieldValue"
                                  :items="getConditionValueItems(condition)"
                                  item-text="text"
                                  item-value="value"
                                  :label="getConditionValueLabel(condition)"
                                  :multiple="isConditionMultiValue(condition)"
                                  :chips="isConditionMultiValue(condition)"
                                  :small-chips="isConditionMultiValue(condition)"
                                  :deletable-chips="isConditionMultiValue(condition)"
                                  :disabled="!canWrite"
                                  :menu-props="getWorkflowDialogMenuProps()"
                                  :no-data-text="$t('wkt.admin.common.table.no_data')"
                                />
                                <v-text-field
                                  v-else-if="isConditionDateField(condition)"
                                  v-model="condition.fieldValue"
                                  type="date"
                                  :label="getConditionValueLabel(condition)"
                                  :readonly="!canWrite"
                                />
                                <v-text-field
                                  v-else-if="isConditionTimeField(condition)"
                                  v-model="condition.fieldValue"
                                  type="time"
                                  :label="getConditionValueLabel(condition)"
                                  :readonly="!canWrite"
                                />
                                <v-combobox
                                  v-else-if="isConditionMultiValue(condition)"
                                  v-model="condition.fieldValue"
                                  multiple
                                  chips
                                  small-chips
                                  deletable-chips
                                  :label="getConditionValueLabel(condition)"
                                  :placeholder="getConditionValuePlaceholder(condition)"
                                  :disabled="!canWrite"
                                  :menu-props="getWorkflowDialogMenuProps()"
                                />
                                <v-text-field
                                  v-else
                                  v-model="condition.fieldValue"
                                  :type="isConditionNumberField(condition) ? 'number' : 'text'"
                                  :label="getConditionValueLabel(condition)"
                                  :placeholder="getConditionValuePlaceholder(condition)"
                                  :readonly="!canWrite"
                                />
                              </v-col>
                            </v-row>
                          </v-card-text>
                        </v-card>

                        <v-btn
                          v-if="canWrite"
                          text
                          color="primary"
                          @click="addActionCondition(action)"
                        >
                          {{ $t('wkt.admin.workflows.actions.add_action_condition') }}
                        </v-btn>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <div>
              <v-btn
                v-if="canWrite"
                text
                color="primary"
                @click="addAction"
              >
                {{ $t('wkt.admin.workflows.actions.add_action') }}
              </v-btn>
            </div>
                </v-tab-item>

                <v-tab-item v-if="workflowForm.id" value="4" class="px-0">
                  <v-alert
                    v-if="!executionLogs.length"
                    dense
                    outlined
                    type="info"
                    class="mt-2"
                  >
                    {{ $t('wkt.admin.workflows.empty.executions') }}
                  </v-alert>

                  <v-simple-table v-else dense>
                    <thead>
                      <tr>
                        <th>{{ $t('wkt.admin.workflows.executions.headers.status') }}</th>
                        <th>{{ $t('wkt.admin.workflows.executions.headers.date') }}</th>
                        <th>{{ $t('wkt.admin.workflows.executions.headers.event') }}</th>
                        <th>{{ $t('wkt.admin.workflows.executions.headers.actions') }}</th>
                        <th>{{ $t('wkt.admin.workflows.executions.headers.result') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="execution in executionLogs"
                        :key="`execution-${execution.executionLog.id}`"
                      >
                        <td class="font-weight-medium">{{ formatExecutionStatus(execution.executionLog.status) }}</td>
                        <td>{{ formatLongDateTime(execution.executionLog.executionDate, true) }}</td>
                        <td>
                          {{ execution.executionLog.eventType }}
                          <span v-if="execution.executionLog.eventSubtype">
                            · {{ execution.executionLog.eventSubtype }}
                          </span>
                        </td>
                        <td>
                          <span v-if="execution.executionLog.actionsTotal != null">
                            {{ execution.executionLog.actionsOk || 0 }}/{{ execution.executionLog.actionsTotal || 0 }} OK
                          </span>
                          <span v-else>--</span>
                        </td>
                        <td>
                          {{ execution.executionLog.errorMessage || buildExecutionResultSummary(execution) || '--' }}
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </v-tab-item>
            </v-tabs-items>

          </v-form>
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-btn
            v-if="canWrite && workflowForm.id"
            depressed
            color="error"
            :loading="deleteLoading"
            @click="deleteSelectedWorkflow"
          >
            <v-icon left>delete</v-icon>
            {{ $t('wkt.admin.common.dialog-buttons.delete') }}
          </v-btn>
          <v-btn
            v-if="canWrite && workflowForm.id"
            text
            color="primary"
            :loading="duplicateLoading"
            @click="duplicateSelectedWorkflow"
          >
            <v-icon left>content_copy</v-icon>
            {{ $t('wkt.admin.workflows.actions.duplicate') }}
          </v-btn>
          <v-spacer />
          <v-btn text @click="closeDetailDialog">
            {{ $t('wkt.admin.common.dialog-buttons.close') }}
          </v-btn>
          <v-btn
            v-if="canWrite"
            color="primary"
            depressed
            :loading="saveLoading"
            @click="saveWorkflow"
          >
            {{ $t('wkt.admin.common.dialog-buttons.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="showAlert"
      :timeout="alertTimeout"
      :color="alertColor">
      <span style="color: #ffffff;">{{ alertText }}</span>
      <v-btn text @click="showAlert = false">
        <span style="color: #ffffff;">{{ $t('wkt.admin.common.dialog-buttons.close') }}</span>
      </v-btn>
    </v-snackbar>

    <ConfirmDialog
      ref="deleteWorkflowConfirmDialog"
      :title="$t('wkt.admin.common.dialog-buttons.delete')"
      :message="$t('wkt.admin.workflows.messages.delete_confirm')"
      :title-ok="$t('wkt.admin.common.dialog-buttons.delete')"
      :title-cancel="$t('wkt.admin.common.dialog-buttons.cancel')"
      @confirmed="confirmDeleteSelectedWorkflow"
    />

    <ConfirmDialog
      ref="disableWorkflowConfirmDialog"
      :title="$t('wkt.admin.workflows.actions.disable')"
      :message="$t('wkt.admin.workflows.messages.disable_confirm')"
      :title-ok="$t('wkt.admin.workflows.actions.disable')"
      :title-cancel="$t('wkt.admin.common.dialog-buttons.cancel')"
      @confirmed="confirmToggleSelectedWorkflow"
    />
  </v-container>
</template>

<script>
import BaseModule from '../common/BaseModule.js'
import ConfirmDialog from '../common/ConfirmDialog.vue'
import api from '../../api/worktime'
import gwt from '../../api/gwt'

const CONDITION_FIELD_ALIASES = {
  extra_hours: 'extraHours',
  workday_date: 'workdayDate',
  is_telework: 'isTelework',
  periodicWeekday: 'periodicDayOfWeek',
  surveyTitle: 'title',
}

const ACTION_TEMPLATE_VARIABLE_ALIASES = {
  firstname: 'employeeFirstname',
  lastname: 'employeeLastname',
  email: 'employeeEmail',
  workplaceId: 'employeeWorkplaceId',
  departmentId: 'employeeDepartmentId',
  legalEntityId: 'employeeLegalEntityId',
  requestType: 'requestTypeId',
}

const CONDITION_FIELD_DEFINITIONS = {
  employeeId: { type: 'catalog', operators: ['EQ', 'NEQ', 'IN'] },
  employeeName: { type: 'text', operators: ['EQ', 'NEQ', 'CONTAINS'] },
  employeeFirstname: { type: 'text', operators: ['EQ', 'NEQ', 'CONTAINS'] },
  employeeLastname: { type: 'text', operators: ['EQ', 'NEQ', 'CONTAINS'] },
  employeeEmail: { type: 'text', operators: ['EQ', 'NEQ', 'CONTAINS'] },
  employeeWorkplaceId: { type: 'catalog', operators: ['EQ', 'NEQ', 'IN'] },
  employeeDepartmentId: { type: 'catalog', operators: ['EQ', 'NEQ', 'IN'] },
  employeeLegalEntityId: { type: 'catalog', operators: ['EQ', 'NEQ', 'IN'] },
  module: { type: 'text', operators: ['EQ', 'NEQ', 'CONTAINS'] },
  isAdminSession: { type: 'boolean', operators: ['EQ', 'NEQ'] },
  requestId: { type: 'number', operators: ['EQ', 'NEQ', 'GT', 'GTE', 'LT', 'LTE', 'IN'] },
  requestTitle: { type: 'text', operators: ['EQ', 'NEQ', 'CONTAINS'] },
  requestTypeId: { type: 'catalog', operators: ['EQ', 'NEQ', 'IN'] },
  requestStatus: { type: 'enum', operators: ['EQ', 'NEQ', 'IN'] },
  projectId: { type: 'number', operators: ['EQ', 'NEQ', 'GT', 'GTE', 'LT', 'LTE', 'IN'] },
  activityTypeId: { type: 'number', operators: ['EQ', 'NEQ', 'GT', 'GTE', 'LT', 'LTE', 'IN'] },
  quantity: { type: 'number', operators: ['EQ', 'NEQ', 'GT', 'GTE', 'LT', 'LTE', 'IN'] },
  hoursBagType: { type: 'enum', operators: ['EQ', 'NEQ', 'IN'] },
  fromDate: { type: 'date', operators: ['EQ', 'NEQ', 'GT', 'GTE', 'LT', 'LTE'] },
  toDate: { type: 'date', operators: ['EQ', 'NEQ', 'GT', 'GTE', 'LT', 'LTE'] },
  documentId: { type: 'number', operators: ['EQ', 'NEQ', 'GT', 'GTE', 'LT', 'LTE', 'IN'] },
  documentName: { type: 'text', operators: ['EQ', 'NEQ', 'CONTAINS'] },
  documentCategory: { type: 'catalog', operators: ['EQ', 'NEQ', 'IN'] },
  documentType: { type: 'enum', operators: ['EQ', 'NEQ', 'IN'] },
  signatureMode: { type: 'enum', operators: ['EQ', 'NEQ', 'IN'] },
  documentCreationDate: { type: 'date', operators: ['EQ', 'NEQ', 'GT', 'GTE', 'LT', 'LTE'] },
  signatureDate: { type: 'date', operators: ['EQ', 'NEQ', 'GT', 'GTE', 'LT', 'LTE'] },
  workdayId: { type: 'number', operators: ['EQ', 'NEQ', 'GT', 'GTE', 'LT', 'LTE', 'IN'] },
  workdayDate: { type: 'date', operators: ['EQ', 'NEQ', 'GT', 'GTE', 'LT', 'LTE'] },
  closed: { type: 'boolean', operators: ['EQ', 'NEQ'] },
  isTelework: { type: 'boolean', operators: ['EQ', 'NEQ'] },
  extraHours: { type: 'number', operators: ['EQ', 'NEQ', 'GT', 'GTE', 'LT', 'LTE', 'IN'] },
  declaredExtraHours: { type: 'number', operators: ['EQ', 'NEQ', 'GT', 'GTE', 'LT', 'LTE', 'IN'] },
  storedExtraHours: { type: 'number', operators: ['EQ', 'NEQ', 'GT', 'GTE', 'LT', 'LTE', 'IN'] },
  totalActivityWorkHours: { type: 'number', operators: ['EQ', 'NEQ', 'GT', 'GTE', 'LT', 'LTE', 'IN'] },
  totalActivityNonWorkHours: { type: 'number', operators: ['EQ', 'NEQ', 'GT', 'GTE', 'LT', 'LTE', 'IN'] },
  approvalRequired: { type: 'boolean', operators: ['EQ', 'NEQ'] },
  additionalHoursCategoryId: { type: 'catalog', operators: ['EQ', 'NEQ', 'IN'] },
  surveyId: { type: 'number', operators: ['EQ', 'NEQ', 'GT', 'GTE', 'LT', 'LTE', 'IN'] },
  title: { type: 'text', operators: ['EQ', 'NEQ', 'CONTAINS'] },
  description: { type: 'text', operators: ['EQ', 'NEQ', 'CONTAINS'] },
  formId: { type: 'number', operators: ['EQ', 'NEQ', 'GT', 'GTE', 'LT', 'LTE', 'IN'] },
  publishStartDate: { type: 'date', operators: ['EQ', 'NEQ', 'GT', 'GTE', 'LT', 'LTE'] },
  publishEndDate: { type: 'date', operators: ['EQ', 'NEQ', 'GT', 'GTE', 'LT', 'LTE'] },
  enabled: { type: 'boolean', operators: ['EQ', 'NEQ'] },
  notified: { type: 'boolean', operators: ['EQ', 'NEQ'] },
  currentResponses: { type: 'number', operators: ['EQ', 'NEQ', 'GT', 'GTE', 'LT', 'LTE', 'IN'] },
  expectedResponses: { type: 'number', operators: ['EQ', 'NEQ', 'GT', 'GTE', 'LT', 'LTE', 'IN'] },
  periodicFrequency: { type: 'enum', operators: ['EQ', 'NEQ', 'IN'] },
  periodicDate: { type: 'date', operators: ['EQ', 'NEQ', 'GT', 'GTE', 'LT', 'LTE'] },
  periodicTime: { type: 'time', operators: ['EQ', 'NEQ'] },
  periodicDayOfWeek: { type: 'enum', operators: ['EQ', 'NEQ', 'IN'] },
  periodicDayOfMonth: { type: 'number', operators: ['EQ', 'NEQ', 'GT', 'GTE', 'LT', 'LTE', 'IN'] },
}

const WORKFLOW_CONDITION_FIELDS_BY_EVENT = {
  PERIODIC_EVENT: ['periodicFrequency', 'periodicDate', 'periodicTime', 'periodicDayOfWeek', 'periodicDayOfMonth'],
  EMPLOYEE_CREATED: ['employeeId', 'employeeName', 'employeeFirstname', 'employeeLastname', 'employeeEmail', 'employeeWorkplaceId', 'employeeDepartmentId', 'employeeLegalEntityId'],
  EMPLOYEE_FIRST_ACCESS: ['employeeId', 'employeeName', 'employeeFirstname', 'employeeLastname', 'employeeEmail', 'employeeWorkplaceId', 'employeeDepartmentId', 'employeeLegalEntityId', 'module', 'isAdminSession'],
  REQUEST_CREATED: ['employeeId', 'employeeName', 'employeeEmail', 'employeeWorkplaceId', 'employeeDepartmentId', 'employeeLegalEntityId', 'requestId', 'requestTitle', 'requestTypeId', 'requestStatus', 'projectId', 'activityTypeId', 'quantity', 'hoursBagType', 'fromDate', 'toDate'],
  REQUEST_APPROVED: ['employeeId', 'employeeName', 'employeeEmail', 'employeeWorkplaceId', 'employeeDepartmentId', 'employeeLegalEntityId', 'requestId', 'requestTitle', 'requestTypeId', 'requestStatus', 'projectId', 'activityTypeId', 'quantity', 'hoursBagType', 'fromDate', 'toDate'],
  REQUEST_REJECTED: ['employeeId', 'employeeName', 'employeeEmail', 'employeeWorkplaceId', 'employeeDepartmentId', 'employeeLegalEntityId', 'requestId', 'requestTitle', 'requestTypeId', 'requestStatus', 'projectId', 'activityTypeId', 'quantity', 'hoursBagType', 'fromDate', 'toDate'],
  DOCUMENT_UPLOADED: ['employeeId', 'employeeName', 'employeeEmail', 'employeeWorkplaceId', 'employeeDepartmentId', 'employeeLegalEntityId', 'documentId', 'documentName', 'documentCategory', 'documentType', 'requestStatus', 'requestTitle', 'documentCreationDate', 'signatureMode'],
  DOCUMENT_SIGNED: ['employeeId', 'employeeName', 'employeeEmail', 'employeeWorkplaceId', 'employeeDepartmentId', 'employeeLegalEntityId', 'documentId', 'documentName', 'documentCategory', 'documentType', 'requestStatus', 'requestTitle', 'documentCreationDate', 'signatureMode', 'signatureDate'],
  WORKDAY_CLOSED: ['employeeId', 'employeeName', 'employeeWorkplaceId', 'employeeDepartmentId', 'employeeLegalEntityId', 'workdayId', 'workdayDate', 'closed', 'isTelework', 'extraHours', 'additionalHoursCategoryId', 'totalActivityWorkHours', 'totalActivityNonWorkHours'],
  OVERTIME_DETECTED: ['employeeId', 'employeeName', 'employeeWorkplaceId', 'employeeDepartmentId', 'employeeLegalEntityId', 'workdayId', 'workdayDate', 'closed', 'declaredExtraHours', 'storedExtraHours', 'approvalRequired', 'additionalHoursCategoryId', 'totalActivityWorkHours', 'totalActivityNonWorkHours'],
  SURVEY_SENT: ['surveyId', 'title', 'description', 'formId', 'publishStartDate', 'publishEndDate', 'enabled', 'notified', 'currentResponses', 'expectedResponses'],
  SURVEY_RESPONDED: ['employeeId', 'employeeName', 'employeeWorkplaceId', 'employeeDepartmentId', 'employeeLegalEntityId', 'surveyId', 'title', 'description', 'formId', 'publishStartDate', 'publishEndDate', 'enabled', 'notified', 'currentResponses', 'expectedResponses'],
}

const ADMIN_RECIPIENT_PERMISSION_MODULES = [
  'home', 'messages', 'news', 'daily', 'monthly', 'employee', 'procedures', 'documents', 'pdocuments',
  'documentsExternal', 'hcalendar', 'holidays', 'staff', 'assessments', 'teams', 'projects', 'planning',
  'surveys', 'partner_customers', 'gconfig', 'profiles', 'workplace',
  'departments', 'legalEntity', 'calendar', 'vpolicy', 'pforms', 'pdefinition', 'activityType',
  'documentCategory', 'documentCategoryExternal', 'chatconfig', 'gcustomize', 'integration', 'license', 'ai',
  'complaint', 'complaintconfig', 'expensesheetcategory', 'expensesheet',
  'paidAbsencesCategory', 'additionalHoursCategory', 'tasks', 'workflows',
]

const ADMIN_RECIPIENT_PERMISSION_LABEL_KEYS = {
  documentsExternal: 'wkt.admin.profiles.edit-window.documents_external',
  partner_customers: 'wkt.admin.profiles.edit-window.partner_customers',
  reportType: 'wkt.admin.profiles.edit-window.reportType',
  adminconfig: 'wkt.admin.profiles.edit-window.adminconfig',
  gconfig: 'wkt.admin.profiles.edit-window.gconfig',
  legalEntity: 'wkt.admin.legal-entities.title',
  vpolicy: 'wkt.admin.profiles.edit-window.vpolicy',
  pforms: 'wkt.admin.profiles.edit-window.pforms',
  pdefinition: 'wkt.admin.profiles.edit-window.pdefinition',
  activityType: 'wkt.admin.profiles.edit-window.activityType',
  documentCategory: 'wkt.admin.profiles.edit-window.documentCategory',
  documentCategoryExternal: 'wkt.admin.profiles.edit-window.document_category_external',
  complaintconfig: 'wkt.admin.profiles.edit-window.complaint_config',
  expensesheetcategory: 'wkt.admin.profiles.edit-window.expenses-categories',
  expensesheet: 'wkt.admin.profiles.edit-window.expenses',
  paidAbsencesCategory: 'wkt.admin.profiles.edit-window.paid-absences-category',
  additionalHoursCategory: 'wkt.admin.profiles.edit-window.additional-hours-type',
}

export default {
  name: 'WorkflowsModule',
  components: {
    ConfirmDialog,
  },
  props: {
    dialogOnly: {
      type: Boolean,
      default: false,
    }
  },
  mixins: [BaseModule],
  computed: {
    isPeriodicEvent() {
      return this.workflowForm.eventType === 'PERIODIC_EVENT'
    },
    conditionFieldItems() {
      const eventType = this.workflowForm && this.workflowForm.eventType
      const eventFields = WORKFLOW_CONDITION_FIELDS_BY_EVENT[eventType] || ['employeeId', 'employeeName']
      const existingFields = [
        ...(this.workflowForm && Array.isArray(this.workflowForm.conditions) ? this.workflowForm.conditions : []),
        ...(this.workflowForm && Array.isArray(this.workflowForm.actionConditions) ? this.workflowForm.actionConditions : []),
      ]
        .map(condition => this.normalizeConditionFieldName(condition && condition.fieldName))
        .filter(Boolean)
      const fields = Array.from(new Set([...eventFields, ...existingFields]))
      return fields.map(value => ({ value, text: value }))
        .map(item => ({
          value: item.value,
          text: this.getConditionFieldLabel(item.value),
        }))
    },
    validationState() {
      return this.getWorkflowValidationState()
    },
    isBaseStepComplete() {
      return !!(this.workflowForm.name && this.workflowForm.eventType)
    },
    isConditionsStepComplete() {
      return this.workflowForm.conditions.every(condition => !this.isConditionFilled(condition) || this.isConditionComplete(condition))
    },
    isActionsStepComplete() {
      return this.workflowForm.actions.length > 0
        && this.workflowForm.actions.every(action => this.isActionValidForStep(action))
        && this.workflowForm.actionConditions.every(condition => !this.isConditionFilled(condition) || this.isConditionComplete(condition))
    },
  },
  data () {
    return {
      apiLoading: false,
      errorMessage: null,
      showAlert: false,
      alertColor: 'error',
      alertText: '',
      alertTimeout: 3000,
      search: '',
      options: { itemsPerPage: 20 },
      totalItems: 0,
      items: [],
      employees: [],
      sessionInformation: null,
      workplaces: [],
      departments: [],
      legalEntities: [],
      documentCategories: [],
      additionalHoursCategories: [],
      workflowSourceDocuments: [],
      workflowForms: [],
      sessionProfile: null,
      canWrite: false,
      detailDialog: false,
      editorStep: '1',
      templateDialog: false,
      detailLoading: false,
      templatesLoading: false,
      saveLoading: false,
      duplicateLoading: false,
      deleteLoading: false,
      toggleLoading: false,
      createFromTemplateLoading: null,
      workflowMetadataLoaded: false,
      workflowMetadataLoading: false,
      activeActionTemplateTarget: null,
      conditionEditorPanel: null,
      actionEditorPanel: 0,
      nextLocalId: -1,
      executionLogs: [],
      workflowTemplates: [],
      periodicConfig: {
        frequency: 'DAILY',
        weekday: 'MONDAY',
        dayOfMonth: 1,
        time: '09:00',
      },
      workflowForm: {
        id: null,
        name: '',
        description: '',
        eventType: '',
        eventSubtype: '',
        enabled: true,
        shared: true,
        conditions: [],
        actions: [],
        actionConditions: [],
      },
      headers: [
        { text: this.$t('wkt.admin.workflows.table.headers.created'), value: 'creationDate', width: '22%' },
        { text: this.$t('wkt.admin.workflows.table.headers.name'), value: 'name', width: '28%' },
        { text: this.$t('wkt.admin.workflows.table.headers.event_type'), value: 'eventType', width: '25%' },
        { text: this.$t('wkt.admin.workflows.table.headers.enabled'), value: 'enabled', width: '10%' },
        { text: this.$t('wkt.admin.workflows.table.headers.next_run'), value: 'nextRunAt', width: '15%' },
        { text: this.$t('wkt.admin.workflows.table.headers.last_update'), value: 'updateDate', width: '15%' }
      ],
      eventTypeItems: [
        { value: 'PERIODIC_EVENT', text: this.$t('wkt.admin.workflows.event_type.PERIODIC_EVENT') },
        { value: 'EMPLOYEE_CREATED', text: this.$t('wkt.admin.workflows.event_type.EMPLOYEE_CREATED') },
        { value: 'EMPLOYEE_FIRST_ACCESS', text: this.$t('wkt.admin.workflows.event_type.EMPLOYEE_FIRST_ACCESS') },
        { value: 'REQUEST_CREATED', text: this.$t('wkt.admin.workflows.event_type.REQUEST_CREATED') },
        { value: 'REQUEST_APPROVED', text: this.$t('wkt.admin.workflows.event_type.REQUEST_APPROVED') },
        { value: 'REQUEST_REJECTED', text: this.$t('wkt.admin.workflows.event_type.REQUEST_REJECTED') },
        { value: 'DOCUMENT_UPLOADED', text: this.$t('wkt.admin.workflows.event_type.DOCUMENT_UPLOADED') },
        { value: 'DOCUMENT_SIGNED', text: this.$t('wkt.admin.workflows.event_type.DOCUMENT_SIGNED') },
        { value: 'WORKDAY_CLOSED', text: this.$t('wkt.admin.workflows.event_type.WORKDAY_CLOSED') },
        { value: 'OVERTIME_DETECTED', text: this.$t('wkt.admin.workflows.event_type.OVERTIME_DETECTED') },
        { value: 'SURVEY_SENT', text: this.$t('wkt.admin.workflows.event_type.SURVEY_SENT') },
        { value: 'SURVEY_RESPONDED', text: this.$t('wkt.admin.workflows.event_type.SURVEY_RESPONDED') },
      ],
      actionTypeItems: [
        { value: 'CREATE_TASK', text: this.$t('wkt.admin.workflows.action_type.CREATE_TASK') },
        { value: 'SEND_EMAIL', text: this.$t('wkt.admin.workflows.action_type.SEND_EMAIL') },
        { value: 'SEND_MESSAGE', text: this.$t('wkt.admin.workflows.action_type.SEND_MESSAGE') },
        { value: 'REQUEST_DOCUMENT', text: this.$t('wkt.admin.workflows.action_type.REQUEST_DOCUMENT') },
        { value: 'SHARE_DOCUMENT', text: this.$t('wkt.admin.workflows.action_type.SHARE_DOCUMENT') },
        { value: 'SEND_FORM', text: this.$t('wkt.admin.workflows.action_type.SEND_FORM') },
      ],
      periodicFrequencyItems: [
        { value: 'DAILY', text: this.$t('wkt.admin.workflows.periodic.frequency.DAILY') },
        { value: 'WEEKLY', text: this.$t('wkt.admin.workflows.periodic.frequency.WEEKLY') },
        { value: 'MONTHLY', text: this.$t('wkt.admin.workflows.periodic.frequency.MONTHLY') },
      ],
      periodicWeekdayItems: [
        { value: 'MONDAY', text: this.$t('wkt.admin.workflows.periodic.weekday.MONDAY') },
        { value: 'TUESDAY', text: this.$t('wkt.admin.workflows.periodic.weekday.TUESDAY') },
        { value: 'WEDNESDAY', text: this.$t('wkt.admin.workflows.periodic.weekday.WEDNESDAY') },
        { value: 'THURSDAY', text: this.$t('wkt.admin.workflows.periodic.weekday.THURSDAY') },
        { value: 'FRIDAY', text: this.$t('wkt.admin.workflows.periodic.weekday.FRIDAY') },
        { value: 'SATURDAY', text: this.$t('wkt.admin.workflows.periodic.weekday.SATURDAY') },
        { value: 'SUNDAY', text: this.$t('wkt.admin.workflows.periodic.weekday.SUNDAY') },
      ],
      recipientTypeItems: [
        { value: 'SUBJECT', text: this.$t('wkt.admin.workflows.recipient_type.SUBJECT') },
        { value: 'MANAGER', text: this.$t('wkt.admin.workflows.recipient_type.MANAGER') },
        { value: 'ADMIN', text: this.$t('wkt.admin.workflows.recipient_type.ADMIN') },
        { value: 'EXPLICIT', text: this.$t('wkt.admin.workflows.recipient_type.EXPLICIT') },
      ],
      signatureModeItems: [
        { value: 'SMS', text: this.$t('wkt.admin.workflows.signature_mode.SMS') },
        { value: 'HANDWRITTEN', text: this.$t('wkt.admin.workflows.signature_mode.HANDWRITTEN') },
        { value: 'WHATSAPP', text: this.$t('wkt.admin.workflows.signature_mode.WHATSAPP') },
      ],
      operatorItems: [
        { value: 'EQ', text: this.$t('wkt.admin.workflows.operators.EQ') },
        { value: 'NEQ', text: this.$t('wkt.admin.workflows.operators.NEQ') },
        { value: 'GT', text: this.$t('wkt.admin.workflows.operators.GT') },
        { value: 'GTE', text: this.$t('wkt.admin.workflows.operators.GTE') },
        { value: 'LT', text: this.$t('wkt.admin.workflows.operators.LT') },
        { value: 'LTE', text: this.$t('wkt.admin.workflows.operators.LTE') },
        { value: 'IN', text: this.$t('wkt.admin.workflows.operators.IN') },
        { value: 'CONTAINS', text: this.$t('wkt.admin.workflows.operators.CONTAINS') },
      ],
      periodicTimeMenu: false,
    }
  },
  mounted() {
    this.loadContext()
    this.loadEmployees()
    this.installShadowMenuWheelPatch({
      menuSelector: '.workflow-dialog-menu',
      handlerKey: '__tweemWorkflowManualWheelHandler'
    })
  },
  beforeDestroy() {
    this.teardownShadowMenuWheelPatch()
  },
  methods: {
    onWheel(event) {
      if (!event) {
        return
      }

      event.stopPropagation()
    },
    doShowAlert(payload) {
      this.alertText = payload.text
      this.alertColor = payload.color
      this.alertTimeout = payload.timeout
      this.showAlert = true
    },
    employeeText(item) {
      if (!item) {
        return ''
      }

      return `${item.lastname || ''}, ${item.firstname || ''}`.trim().replace(/^, /, '')
    },
    loadContext() {
      this.sessionInformation = gwt.getSessionInformation()
      this.sessionProfile = gwt.getSessionProfile()
      this.canWrite = !!(this.sessionProfile && this.matchRoleModify(this.sessionProfile, ['workflows']))
      this.loadWorkflowMetadata()
      this.loadWorkflowCatalogOptions()
      this.loadConditionCatalogOptions()
    },
    sortOptionItems(items) {
      return (items || []).slice().sort((left, right) => String(left.text || '').localeCompare(String(right.text || '')))
    },
    workplaceText(item) {
      if (!item) {
        return ''
      }

      return item.name || item.description || item.internalCode || `#${item.id}`
    },
    departmentText(item) {
      if (!item) {
        return ''
      }

      return item.name || item.description || item.internalCode || `#${item.id}`
    },
    legalEntityText(item) {
      if (!item) {
        return ''
      }

      return item.legalEntityName || item.name || item.description || item.legalEntityCode || item.internalCode || `#${item.id}`
    },
    requestTypeText(item) {
      if (!item) {
        return ''
      }

      return item.name || item.description || `#${item.id}`
    },
    documentCategoryText(item) {
      if (!item) {
        return ''
      }

      return item.name || item.description || item.internalCode || `#${item.id}`
    },
    additionalHoursCategoryText(item) {
      if (!item) {
        return ''
      }

      return item.name || item.description || item.internalCode || `#${item.id}`
    },
    loadConditionCatalogOptions() {
      const workplaces = gwt.getWorkplacesByProfile()
      const departments = gwt.getDepartmentsByProfile()
      const legalEntities = gwt.getLegalEntities()
      const workplaceSource = Array.isArray(workplaces) && workplaces.length ? workplaces : gwt.getWorkplaces()
      const departmentSource = Array.isArray(departments) && departments.length ? departments : gwt.getDepartments()

      this.workplaces = this.sortOptionItems((Array.isArray(workplaceSource) ? workplaceSource : [])
        .map(item => ({ value: String(item.id), text: this.workplaceText(item) })))
      this.departments = this.sortOptionItems((Array.isArray(departmentSource) ? departmentSource : [])
        .map(item => ({ value: String(item.id), text: this.departmentText(item) })))
      this.legalEntities = this.sortOptionItems((Array.isArray(legalEntities) ? legalEntities : [])
        .map(item => ({ value: String(item.id), text: this.legalEntityText(item) })))
      this.documentCategories = this.sortOptionItems((((this.sessionInformation && this.sessionInformation.documentCategories) || []))
        .map(item => ({ value: String(item.id), text: this.documentCategoryText(item) })))

      api.getWorkPlacesWithoutGlobalFilters(
        data => {
          this.workplaces = this.sortOptionItems((Array.isArray(data) ? data : [])
            .map(item => ({ value: String(item.id), text: this.workplaceText(item) })))
        },
        () => {
          // Keep GWT fallback already loaded above.
        })

      api.getLegalEntitiesWithoutGlobalFilters(
        data => {
          this.legalEntities = this.sortOptionItems((Array.isArray(data) ? data : [])
            .map(item => ({ value: String(item.id), text: this.legalEntityText(item) })))
        },
        () => {
          // Keep GWT fallback already loaded above.
        })

      api.getAdditionalHoursCategories(
        data => {
          this.additionalHoursCategories = this.sortOptionItems((Array.isArray(data) ? data : [])
            .filter(item => item && item.isEnabled !== false)
            .map(item => ({ value: String(item.id), text: this.additionalHoursCategoryText(item) })))
        },
        () => {
          this.additionalHoursCategories = []
        })
    },
    sourceDocumentText(item) {
      if (!item) {
        return ''
      }

      const documentName = item.documentName || item.requestTitle || `#${item.id}`
      return item.documentCategory ? `${documentName} · #${item.id}` : documentName
    },
    workflowFormText(item) {
      if (!item) {
        return ''
      }

      return item.name || `#${item.id}`
    },
    loadWorkflowCatalogOptions() {
      api.getWorkflowSourceDocuments(
        data => {
          this.workflowSourceDocuments = Array.isArray(data)
            ? data.slice().sort((left, right) => this.sourceDocumentText(left).localeCompare(this.sourceDocumentText(right)))
            : []
        },
        () => {
          this.workflowSourceDocuments = []
        })

      api.getWorkflowForms(
        data => {
          this.workflowForms = Array.isArray(data)
            ? data.slice().sort((left, right) => this.workflowFormText(left).localeCompare(this.workflowFormText(right)))
            : []
        },
        () => {
          this.workflowForms = []
        })
    },
    loadEmployees() {
      const departmentsByProfile = gwt.getDepartmentsByProfile()
      const allDepartments = gwt.getDepartments()
      const fallbackDepartments = departmentsByProfile && departmentsByProfile.length ? departmentsByProfile : allDepartments

      if (fallbackDepartments && fallbackDepartments.length) {
        this.applyDepartmentCatalog(fallbackDepartments)
      }

      api.getStaffWithoutGlobalFilters(
        data => {
          this.applyDepartmentCatalog(data || [])
        },
        err => {
          this.doShowAlert({ text: err.message || err, color: 'error', timeout: 3000 })
        })
    },
    applyDepartmentCatalog(departments) {
      const normalizedDepartments = Array.isArray(departments) ? departments : []
      this.departments = this.sortOptionItems(normalizedDepartments
        .map(item => ({ value: String(item.id), text: this.departmentText(item) })))
      this.setEmployeesFromDepartments(normalizedDepartments)
    },
    setEmployeesFromDepartments(departments) {
      const employees = []
      const seen = {}
      const normalizedDepartments = departments || []

      normalizedDepartments.forEach(department => {
        (department.employees || []).forEach(employee => {
          if (!seen[employee.id]) {
            seen[employee.id] = true
            employees.push(employee)
          }
        })
      })

      this.employees = employees.sort((a, b) => this.employeeText(a).localeCompare(this.employeeText(b)))
    },
    loadWorkflowMetadata() {
      if (this.workflowMetadataLoaded || this.workflowMetadataLoading) {
        return
      }

      this.workflowMetadataLoading = true
      let pendingRequests = 2
      const finish = () => {
        pendingRequests -= 1
        if (pendingRequests <= 0) {
          this.workflowMetadataLoading = false
          this.workflowMetadataLoaded = true
        }
      }

      api.getWorkflowEventTypes(
        data => {
          const items = this.buildWorkflowOptionItems(data, 'event_type')
          if (items.length) {
            this.eventTypeItems = items
          }
          finish()
        },
        () => {
          finish()
        })

      api.getWorkflowActionTypes(
        data => {
          const items = this.buildWorkflowOptionItems(data, 'action_type')
          if (items.length) {
            this.actionTypeItems = items
          }
          finish()
        },
        () => {
          finish()
        })
    },
    getWorkflowOptionLabel(prefix, value) {
      if (!value) {
        return ''
      }

      const key = `wkt.admin.workflows.${prefix}.${value}`
      const translated = this.$t(key)
      return translated === key ? value : translated
    },
    buildWorkflowOptionItems(values, prefix) {
      if (!Array.isArray(values)) {
        return []
      }

      return values
        .filter(value => typeof value === 'string' && value.trim() !== '')
        .map(value => ({
          value,
          text: this.getWorkflowOptionLabel(prefix, value),
        }))
    },
    getDefaultEventTypeValue() {
      return this.eventTypeItems && this.eventTypeItems.length
        ? this.eventTypeItems[0].value
        : 'EMPLOYEE_CREATED'
    },
    formatEventType(eventType) {
      return eventType
        ? this.getWorkflowOptionLabel('event_type', eventType)
        : '--'
    },
    formatPeriodicSubtype(eventSubtype) {
      const config = this.parsePeriodicSubtype(eventSubtype)
      if (!config) {
        return eventSubtype || '--'
      }

      if (config.frequency === 'DAILY') {
        return `${this.$t('wkt.admin.workflows.periodic.frequency.DAILY')} · ${config.time}`
      }

      if (config.frequency === 'WEEKLY') {
        return `${this.$t('wkt.admin.workflows.periodic.frequency.WEEKLY')} · ${this.getWorkflowOptionLabel('periodic.weekday', config.weekday)} · ${config.time}`
      }

      return `${this.$t('wkt.admin.workflows.periodic.frequency.MONTHLY')} · ${config.dayOfMonth} · ${config.time}`
    },
    formatActionType(actionType) {
      return actionType
        ? this.getWorkflowOptionLabel('action_type', actionType)
        : '--'
    },
    formatRecipientType(recipientType) {
      return recipientType
        ? this.getWorkflowOptionLabel('recipient_type', recipientType)
        : this.$t('wkt.admin.workflows.summary.no_recipient')
    },
    buildActionSummary(action) {
      const actionType = this.formatActionType(action.actionType)
      const recipientType = this.formatRecipientType(action.recipientType)
      const configSummary = this.getActionConfigSummary(action)
      return configSummary
        ? `${actionType} -> ${recipientType} · ${configSummary}`
        : `${actionType} -> ${recipientType}`
    },
    humanizeConditionFieldName(fieldName) {
      return String(fieldName || '')
        .replace(/_/g, ' ')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/\b\w/g, letter => letter.toUpperCase())
    },
    normalizeConditionFieldName(fieldName) {
      const normalizedFieldName = String(fieldName || '').trim()
      return CONDITION_FIELD_ALIASES[normalizedFieldName] || normalizedFieldName
    },
    getConditionFieldDefinition(fieldName) {
      return CONDITION_FIELD_DEFINITIONS[this.normalizeConditionFieldName(fieldName)] || {
        type: 'text',
        operators: ['EQ', 'NEQ', 'CONTAINS'],
      }
    },
    getConditionFieldLabel(fieldName) {
      const normalizedFieldName = this.normalizeConditionFieldName(fieldName)
      const translationKey = `wkt.admin.workflows.condition_fields.${normalizedFieldName}`
      const translated = this.$t(translationKey)
      return translated === translationKey ? this.humanizeConditionFieldName(normalizedFieldName) : translated
    },
    getActionTemplateVariableLabel(variableName) {
      const mappedFieldName = ACTION_TEMPLATE_VARIABLE_ALIASES[variableName] || variableName
      return this.getConditionFieldLabel(mappedFieldName)
    },
    getActionTemplateVariableItems(action) {
      if (!action) {
        return []
      }

      const eventType = this.workflowForm && this.workflowForm.eventType
      const eventFields = WORKFLOW_CONDITION_FIELDS_BY_EVENT[eventType] || []
      const hasEmployeeContext = eventFields.includes('employeeId')
      const variables = [
        'workflowId',
        'workflowName',
        'eventType',
        'eventSubtype',
        ...eventFields,
      ]

      if (hasEmployeeContext) {
        variables.push('employeeFirstname', 'employeeLastname')
      }

      if (hasEmployeeContext || eventFields.includes('employeeFirstname')) {
        variables.push('firstname')
      }
      if (hasEmployeeContext || eventFields.includes('employeeLastname')) {
        variables.push('lastname')
      }
      if (hasEmployeeContext && !eventFields.includes('employeeEmail')) {
        variables.push('employeeEmail')
      }
      if (hasEmployeeContext || eventFields.includes('employeeEmail')) {
        variables.push('email')
      }
      if (hasEmployeeContext && !eventFields.includes('employeeWorkplaceId')) {
        variables.push('employeeWorkplaceId')
      }
      if (hasEmployeeContext || eventFields.includes('employeeWorkplaceId')) {
        variables.push('workplaceId')
      }
      if (hasEmployeeContext && !eventFields.includes('employeeDepartmentId')) {
        variables.push('employeeDepartmentId')
      }
      if (hasEmployeeContext || eventFields.includes('employeeDepartmentId')) {
        variables.push('departmentId')
      }
      if (hasEmployeeContext && !eventFields.includes('employeeLegalEntityId')) {
        variables.push('employeeLegalEntityId')
      }
      if (hasEmployeeContext || eventFields.includes('employeeLegalEntityId')) {
        variables.push('legalEntityId')
      }
      if (eventFields.includes('requestTypeId')) {
        variables.push('requestType')
      }

      return Array.from(new Set(variables.filter(Boolean))).map(variableName => ({
        value: variableName,
        token: `[[${variableName}]]`,
        text: this.getActionTemplateVariableLabel(variableName),
      }))
    },
    getActionTemplateFieldKeys(action) {
      const actionType = action && action.actionType ? action.actionType : null
      const fieldKeysByType = {
        CREATE_TASK: ['title', 'description'],
        SEND_EMAIL: ['subject', 'body'],
        SEND_MESSAGE: ['title', 'body'],
        REQUEST_DOCUMENT: ['requestTitle', 'requestMessage'],
        SHARE_DOCUMENT: ['requestTitle', 'requestMessage'],
        SEND_FORM: ['requestTitle', 'requestMessage'],
      }

      return fieldKeysByType[actionType] || []
    },
    getActionTemplateInputId(action, fieldKey) {
      if (!action || !fieldKey) {
        return ''
      }

      return `workflow-action-template-${action.localId}-${fieldKey}`
    },
    registerActionTemplateTarget(action, fieldKey, event) {
      if (!action || !fieldKey) {
        return
      }

      const target = event && event.target
      const isTextInput = target && typeof target.selectionStart === 'number' && typeof target.selectionEnd === 'number'
      this.activeActionTemplateTarget = {
        actionLocalId: action.localId,
        fieldKey,
        selectionStart: isTextInput ? target.selectionStart : null,
        selectionEnd: isTextInput ? target.selectionEnd : null,
      }
    },
    insertActionTemplateVariable(action, token) {
      if (!action || !token) {
        return
      }

      const availableFieldKeys = this.getActionTemplateFieldKeys(action)
      if (!availableFieldKeys.length) {
        return
      }

      const activeTarget = this.activeActionTemplateTarget
      const targetFieldKey = activeTarget
        && activeTarget.actionLocalId === action.localId
        && availableFieldKeys.includes(activeTarget.fieldKey)
        ? activeTarget.fieldKey
        : availableFieldKeys[0]

      const currentValue = String(this.getActionConfigValue(action, targetFieldKey) || '')
      const selectionStart = activeTarget
        && activeTarget.actionLocalId === action.localId
        && activeTarget.fieldKey === targetFieldKey
        && typeof activeTarget.selectionStart === 'number'
        ? activeTarget.selectionStart
        : currentValue.length
      const selectionEnd = activeTarget
        && activeTarget.actionLocalId === action.localId
        && activeTarget.fieldKey === targetFieldKey
        && typeof activeTarget.selectionEnd === 'number'
        ? activeTarget.selectionEnd
        : selectionStart

      const nextValue = `${currentValue.slice(0, selectionStart)}${token}${currentValue.slice(selectionEnd)}`
      const nextCursorPosition = selectionStart + token.length

      this.updateActionConfigValue(action, targetFieldKey, nextValue)
      this.activeActionTemplateTarget = {
        actionLocalId: action.localId,
        fieldKey: targetFieldKey,
        selectionStart: nextCursorPosition,
        selectionEnd: nextCursorPosition,
      }

      this.$nextTick(() => {
        const input = document.getElementById(this.getActionTemplateInputId(action, targetFieldKey))
        if (input && typeof input.focus === 'function') {
          input.focus()
        }
        if (input && typeof input.setSelectionRange === 'function') {
          input.setSelectionRange(nextCursorPosition, nextCursorPosition)
        }
      })
    },
    getConditionOperatorItems(condition) {
      const operators = this.getConditionFieldDefinition(condition && condition.fieldName).operators || []
      return this.operatorItems.filter(item => operators.includes(item.value))
    },
    getDefaultConditionOperator(fieldName) {
      const operators = this.getConditionFieldDefinition(fieldName).operators || []
      return operators.length ? operators[0] : 'EQ'
    },
    normalizeDateValueForInput(rawValue) {
      const match = String(rawValue || '').trim().match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/)
      if (!match) {
        return String(rawValue || '').trim()
      }

      return `${match[1]}-${String(Number(match[2])).padStart(2, '0')}-${String(Number(match[3])).padStart(2, '0')}`
    },
    normalizeDateValueForSave(rawValue) {
      const match = String(rawValue || '').trim().match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/)
      if (!match) {
        return String(rawValue || '').trim()
      }

      return `${match[1]}-${Number(match[2])}-${Number(match[3])}`
    },
    splitConditionValueList(fieldValue) {
      return String(fieldValue || '')
        .split(',')
        .map(item => String(item || '').trim())
        .filter(item => item !== '')
    },
    normalizeConditionSingleValueForUi(fieldName, fieldValue) {
      const definition = this.getConditionFieldDefinition(fieldName)
      const normalizedValue = String(fieldValue == null ? '' : fieldValue).trim()

      if (!normalizedValue) {
        return ''
      }

      if (definition.type === 'date') {
        return this.normalizeDateValueForInput(normalizedValue)
      }

      if (definition.type === 'boolean') {
        return normalizedValue === 'true' ? 'true' : (normalizedValue === 'false' ? 'false' : '')
      }

      return normalizedValue
    },
    normalizeConditionFieldValueForUi(fieldName, operator, fieldValue) {
      if (operator === 'IN') {
        const sourceValues = Array.isArray(fieldValue) ? fieldValue : this.splitConditionValueList(fieldValue)
        return sourceValues
          .map(item => this.normalizeConditionSingleValueForUi(fieldName, item))
          .filter(item => item !== '')
      }

      const singleValue = Array.isArray(fieldValue) ? fieldValue[0] : fieldValue
      return this.normalizeConditionSingleValueForUi(fieldName, singleValue)
    },
    normalizeCondition(condition) {
      const normalizedFieldName = this.normalizeConditionFieldName(condition && condition.fieldName)
      const normalizedOperator = this.getConditionOperatorItems({ fieldName: normalizedFieldName, operator: condition && condition.operator })
        .some(item => item.value === (condition && condition.operator))
        ? condition.operator
        : this.getDefaultConditionOperator(normalizedFieldName)

      return {
        ...condition,
        fieldName: normalizedFieldName,
        operator: normalizedOperator,
        fieldValue: this.normalizeConditionFieldValueForUi(normalizedFieldName, normalizedOperator, condition && condition.fieldValue),
      }
    },
    handleConditionFieldChange(condition) {
      if (!condition) {
        return
      }

      const normalizedCondition = this.normalizeCondition(condition)
      condition.fieldName = normalizedCondition.fieldName
      condition.operator = normalizedCondition.operator
      condition.fieldValue = normalizedCondition.fieldValue
    },
    handleConditionOperatorChange(condition) {
      if (!condition) {
        return
      }

      condition.fieldValue = this.normalizeConditionFieldValueForUi(condition.fieldName, condition.operator, condition.fieldValue)
    },
    getConditionValueItems(condition) {
      const fieldName = this.normalizeConditionFieldName(condition && condition.fieldName)
      const eventType = this.workflowForm && this.workflowForm.eventType
      const procedureStatusItems = [
        { value: '-1', text: this.$t('wkt.admin.procedures.request.preparing') },
        { value: '0', text: this.$t('wkt.admin.procedures.request.pending') },
        { value: '1', text: this.$t('wkt.admin.procedures.request.approved') },
        { value: '2', text: this.$t('wkt.admin.procedures.request.denied') },
        { value: '3', text: this.$t('wkt.admin.procedures.request.canceled') },
        { value: '4', text: this.$t('wkt.admin.procedures.request.processed') },
      ]
      const documentStatusItems = [
        { value: '0', text: this.$t('wkt.admin.workflows.document_request_status.NONE') },
        { value: '1', text: this.$t('wkt.admin.workflows.document_request_status.PENDING') },
        { value: '2', text: this.$t('wkt.admin.workflows.document_request_status.CANCELED') },
        { value: '3', text: this.$t('wkt.admin.workflows.document_request_status.COMPLETED') },
      ]
      const hoursBagTypeItems = [
        { value: '1', text: this.$t('wkt.admin.workflows.hours_bag_type.1') },
        { value: '2', text: this.$t('wkt.admin.workflows.hours_bag_type.2') },
        { value: '3', text: this.$t('wkt.admin.workflows.hours_bag_type.3') },
        { value: '4', text: this.$t('wkt.admin.workflows.hours_bag_type.4') },
        { value: '5', text: this.$t('wkt.admin.workflows.hours_bag_type.5') },
        { value: '6', text: this.$t('wkt.admin.workflows.hours_bag_type.6') },
        { value: '7', text: this.$t('wkt.admin.workflows.hours_bag_type.7') },
      ]

      switch (fieldName) {
        case 'employeeId':
          return this.employees.map(item => ({ value: String(item.id), text: this.employeeText(item) }))
        case 'employeeWorkplaceId':
          return this.workplaces
        case 'employeeDepartmentId':
          return this.departments
        case 'employeeLegalEntityId':
          return this.legalEntities
        case 'requestTypeId':
          return this.sortOptionItems((((this.sessionInformation && this.sessionInformation.procedures) || []))
            .map(item => ({ value: String(item.id), text: this.requestTypeText(item) })))
        case 'requestStatus':
          return ['DOCUMENT_UPLOADED', 'DOCUMENT_SIGNED'].includes(eventType) ? documentStatusItems : procedureStatusItems
        case 'documentCategory':
          return this.documentCategories
        case 'documentType':
          return [
            { value: '0', text: this.$t('wkt.admin.documents.documents-utils.type_description.file') },
            { value: '1', text: this.$t('wkt.admin.documents.documents-utils.type_description.url') },
            { value: '2', text: this.$t('wkt.admin.documents.documents-utils.type_description.request') },
            { value: '3', text: this.$t('wkt.admin.documents.documents-utils.type_description.signature') },
            { value: '4', text: this.$t('wkt.admin.documents.documents-utils.type_description.procedure') },
            { value: '5', text: this.$t('wkt.admin.documents.documents-utils.type_description.form') },
          ]
        case 'signatureMode':
          return [
            { value: '0', text: this.$t('wkt.admin.workflows.document_signature_mode.NONE') },
            { value: '1', text: this.$t('wkt.admin.workflows.signature_mode.SMS') },
            { value: '2', text: this.$t('wkt.admin.workflows.signature_mode.HANDWRITTEN') },
            { value: '3', text: this.$t('wkt.admin.workflows.signature_mode.WHATSAPP') },
          ]
        case 'hoursBagType':
          return hoursBagTypeItems
        case 'additionalHoursCategoryId':
          return this.additionalHoursCategories
        case 'periodicFrequency':
          return this.periodicFrequencyItems
        case 'periodicDayOfWeek':
          return this.periodicWeekdayItems
        case 'isAdminSession':
        case 'approvalRequired':
        case 'closed':
        case 'isTelework':
        case 'enabled':
        case 'notified':
          return [
            { value: 'true', text: this.$t('wkt.admin.common.yes') },
            { value: 'false', text: this.$t('wkt.admin.common.no') },
          ]
        default:
          return []
      }
    },
    usesSelectableConditionValues(condition) {
      const definition = this.getConditionFieldDefinition(condition && condition.fieldName)
      return ['catalog', 'enum', 'boolean'].includes(definition.type) && this.getConditionValueItems(condition).length > 0
    },
    isConditionMultiValue(condition) {
      return !!(condition && condition.operator === 'IN')
    },
    isConditionDateField(condition) {
      return this.getConditionFieldDefinition(condition && condition.fieldName).type === 'date'
    },
    isConditionTimeField(condition) {
      return this.getConditionFieldDefinition(condition && condition.fieldName).type === 'time'
    },
    isConditionNumberField(condition) {
      return this.getConditionFieldDefinition(condition && condition.fieldName).type === 'number'
    },
    getConditionValueLabel(condition) {
      return this.$t(condition && condition.operator === 'IN'
        ? 'wkt.admin.workflows.fields.values'
        : 'wkt.admin.workflows.fields.value')
    },
    formatConditionValueSummary(condition) {
      if (!condition) {
        return '--'
      }

      const rawValue = condition.fieldValue
      if (Array.isArray(rawValue)) {
        if (!rawValue.length) {
          return '--'
        }

        return rawValue.map(item => this.getConditionValueDisplayText(condition, item)).join(', ')
      }

      if (rawValue == null || rawValue === '') {
        return '--'
      }

      return this.getConditionValueDisplayText(condition, rawValue)
    },
    getConditionValueDisplayText(condition, value) {
      const normalizedValue = String(value == null ? '' : value).trim()
      if (!normalizedValue) {
        return '--'
      }

      const match = this.getConditionValueItems(condition).find(item => item.value === normalizedValue)
      return match ? match.text : normalizedValue
    },
    buildConditionSummary(condition) {
      if (!condition) {
        return '--'
      }

      return [
        this.getConditionFieldLabel(condition.fieldName) || '--',
        this.formatConditionOperator(condition.operator),
        this.formatConditionValueSummary(condition),
      ].join(' · ')
    },
    formatConditionOperator(operator) {
      if (!operator) {
        return '--'
      }

      const match = (this.operatorItems || []).find(item => item.value === operator)
      return match ? match.text : operator
    },
    getActionConfigSummary(action) {
      const validation = this.parseJsonSafely(action && action.configText ? action.configText : '')
      if (!validation.valid || !validation.value || typeof validation.value !== 'object') {
        return ''
      }

      const summaryKeysByType = {
        CREATE_TASK: ['title', 'description'],
        SEND_EMAIL: ['subject'],
        SEND_MESSAGE: ['title', 'body'],
        REQUEST_DOCUMENT: ['requestTitle', 'requestMessage'],
        SHARE_DOCUMENT: ['requestTitle', 'requestMessage'],
        SEND_FORM: ['requestTitle', 'requestMessage'],
      }

      const preferredKeys = summaryKeysByType[action && action.actionType ? action.actionType : ''] || []
      const summaryKey = preferredKeys.find(key => {
        const value = validation.value[key]
        return typeof value === 'string' && value.trim() !== ''
      })

      if (!summaryKey) {
        return ''
      }

      const rawValue = validation.value[summaryKey].trim()
      return rawValue.length > 90 ? `${rawValue.slice(0, 87)}...` : rawValue
    },
    getRecommendedActionTypes(eventType) {
      const recommendationsByEvent = {
        PERIODIC_EVENT: ['CREATE_TASK', 'SEND_MESSAGE', 'SEND_EMAIL'],
        EMPLOYEE_CREATED: ['SEND_EMAIL', 'CREATE_TASK', 'SEND_FORM'],
        EMPLOYEE_FIRST_ACCESS: ['CREATE_TASK', 'SEND_EMAIL', 'SEND_FORM'],
        REQUEST_CREATED: ['CREATE_TASK', 'SEND_MESSAGE'],
        REQUEST_APPROVED: ['CREATE_TASK', 'SEND_MESSAGE', 'SHARE_DOCUMENT'],
        REQUEST_REJECTED: ['SEND_MESSAGE', 'CREATE_TASK'],
        DOCUMENT_UPLOADED: ['CREATE_TASK', 'REQUEST_DOCUMENT', 'SHARE_DOCUMENT'],
        DOCUMENT_SIGNED: ['CREATE_TASK', 'SEND_EMAIL', 'SHARE_DOCUMENT'],
        WORKDAY_CLOSED: ['CREATE_TASK', 'SEND_MESSAGE'],
        OVERTIME_DETECTED: ['CREATE_TASK', 'SEND_EMAIL'],
        SURVEY_SENT: ['SEND_MESSAGE', 'SEND_EMAIL', 'SEND_FORM'],
        SURVEY_RESPONDED: ['CREATE_TASK', 'SEND_EMAIL'],
      }

      return recommendationsByEvent[eventType] || ['CREATE_TASK', 'SEND_EMAIL']
    },
    getRecipientTypeItems() {
      if (!this.isPeriodicEvent) {
        return this.recipientTypeItems
      }

      return this.recipientTypeItems.filter(item => item.value !== 'SUBJECT' && item.value !== 'MANAGER')
    },
    getAdminRecipientPermissionModuleItems() {
      return ADMIN_RECIPIENT_PERMISSION_MODULES.map(value => ({
        value,
        text: this.getAdminRecipientPermissionModuleLabel(value),
      }))
    },
    getAdminRecipientPermissionModuleLabel(value) {
      const translationKey = ADMIN_RECIPIENT_PERMISSION_LABEL_KEYS[value] || `wkt.admin.profiles.edit-window.${value}`
      const translated = this.$t(translationKey)
      if (translated !== translationKey) {
        return translated
      }

      return String(value || '')
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\b\w/g, letter => letter.toUpperCase())
    },
    getDefaultAdminRecipientPermissionModule(eventType) {
      if (['EMPLOYEE_CREATED', 'EMPLOYEE_FIRST_ACCESS'].includes(eventType)) {
        return 'staff'
      }
      if (['REQUEST_CREATED', 'REQUEST_APPROVED', 'REQUEST_REJECTED'].includes(eventType)) {
        return 'procedures'
      }
      if (['DOCUMENT_UPLOADED', 'DOCUMENT_SIGNED'].includes(eventType)) {
        return 'documents'
      }
      if (['WORKDAY_CLOSED', 'OVERTIME_DETECTED'].includes(eventType)) {
        return 'daily'
      }
      if (['SURVEY_SENT', 'SURVEY_RESPONDED'].includes(eventType)) {
        return 'surveys'
      }
      return 'tasks'
    },
    getAdminRecipientPermissionModule(action) {
      const config = this.getParsedRecipientConfig(action)
      if (typeof config.permissionModule === 'string' && config.permissionModule.trim()) {
        return config.permissionModule.trim()
      }
      return this.getDefaultAdminRecipientPermissionModule(this.workflowForm.eventType)
    },
    updateAdminRecipientPermissionModule(action, value) {
      if (!action) {
        return
      }

      const config = this.getParsedRecipientConfig(action)
      config.permissionModule = value || this.getDefaultAdminRecipientPermissionModule(this.workflowForm.eventType)
      action.recipientConfigText = this.serializeJsonObject(config)
    },
    ensureAdminRecipientConfig(action, eventType) {
      if (!action || action.recipientType !== 'ADMIN') {
        return
      }

      const recipientValidation = this.parseJsonSafely(action.recipientConfigText)
      if (recipientValidation.valid && recipientValidation.value && typeof recipientValidation.value.permissionModule === 'string'
        && recipientValidation.value.permissionModule.trim() !== '') {
        return
      }

      action.recipientConfigText = this.serializeJsonObject({
        permissionModule: this.getDefaultAdminRecipientPermissionModule(eventType || this.workflowForm.eventType),
      })
    },
    getOrderedActionTypeItems() {
      const recommended = this.getRecommendedActionTypes(this.workflowForm.eventType)
      const recommendedSet = new Set(recommended)
      const recommendedItems = []
      const remainingItems = []

      this.actionTypeItems.forEach(item => {
        if (recommendedSet.has(item.value)) {
          recommendedItems.push(item)
        } else {
          remainingItems.push(item)
        }
      })

      recommendedItems.sort((left, right) => recommended.indexOf(left.value) - recommended.indexOf(right.value))
      return recommendedItems.concat(remainingItems)
    },
    getWorkflowDialogMenuProps(overrides = {}) {
      return {
        offsetY: true,
        zIndex: 260,
        contentClass: 'workflow-dialog-menu',
        ...overrides,
      }
    },
    getDefaultPeriodicConfig() {
      return {
        frequency: 'DAILY',
        weekday: 'MONDAY',
        dayOfMonth: 1,
        time: '09:00',
      }
    },
    normalizePeriodicTime(rawValue, fallback = '09:00') {
      const normalizedValue = String(rawValue || '').trim()
      const match = normalizedValue.match(/^([01]\d|2[0-3]):([0-5]\d)$/)
      if (!match) {
        return fallback
      }

      return `${match[1]}:${match[2]}`
    },
    isValidPeriodicTime(rawValue) {
      return /^([01]\d|2[0-3]):([0-5]\d)$/.test(String(rawValue || '').trim())
    },
    parsePeriodicSubtype(eventSubtype) {
      if (!eventSubtype || !String(eventSubtype).trim()) {
        return this.getDefaultPeriodicConfig()
      }

      const normalizedSubtype = String(eventSubtype).trim().toUpperCase()
      const defaultConfig = this.getDefaultPeriodicConfig()
      const subtypeParts = normalizedSubtype.split('@')
      const schedulePart = subtypeParts[0]
      const periodicTime = subtypeParts.length > 1
        ? this.normalizePeriodicTime(subtypeParts[1], defaultConfig.time)
        : defaultConfig.time

      if (schedulePart === 'DAILY') {
        return {
          ...defaultConfig,
          time: periodicTime,
        }
      }

      if (schedulePart.startsWith('WEEKLY:')) {
        const weekday = schedulePart.split(':')[1] || 'MONDAY'
        return {
          frequency: 'WEEKLY',
          weekday,
          dayOfMonth: 1,
          time: periodicTime,
        }
      }

      if (schedulePart.startsWith('MONTHLY:')) {
        const rawDayOfMonth = Number(schedulePart.split(':')[1] || 1)
        return {
          frequency: 'MONTHLY',
          weekday: 'MONDAY',
          dayOfMonth: Number.isNaN(rawDayOfMonth) ? 1 : rawDayOfMonth,
          time: periodicTime,
        }
      }

      return null
    },
    serializePeriodicSubtype(config) {
      const safeConfig = config || this.getDefaultPeriodicConfig()
      const periodicTime = this.normalizePeriodicTime(safeConfig.time, this.getDefaultPeriodicConfig().time)
      if (safeConfig.frequency === 'WEEKLY') {
        return `WEEKLY:${safeConfig.weekday || 'MONDAY'}@${periodicTime}`
      }
      if (safeConfig.frequency === 'MONTHLY') {
        const rawDayOfMonth = Number(safeConfig.dayOfMonth || 1)
        const dayOfMonth = Math.min(31, Math.max(1, Number.isNaN(rawDayOfMonth) ? 1 : rawDayOfMonth))
        return `MONTHLY:${dayOfMonth}@${periodicTime}`
      }
      return `DAILY@${periodicTime}`
    },
    syncPeriodicSubtype() {
      if (!this.isPeriodicEvent) {
        return
      }

      this.workflowForm.eventSubtype = this.serializePeriodicSubtype(this.periodicConfig)
    },
    handlePeriodicConfigChange() {
      if (!this.isPeriodicEvent) {
        return
      }

      if (this.periodicConfig.frequency !== 'WEEKLY' && !this.periodicConfig.weekday) {
        this.periodicConfig.weekday = 'MONDAY'
      }

      if (this.periodicConfig.frequency !== 'MONTHLY') {
        this.periodicConfig.dayOfMonth = 1
      }

      if (!this.isValidPeriodicTime(this.periodicConfig.time)) {
        this.periodicConfig.time = this.getDefaultPeriodicConfig().time
      }

      this.syncPeriodicSubtype()
    },
    updatePeriodicTime(value) {
      if (!value) {
        return
      }
      this.periodicConfig.time = value
      this.periodicTimeMenu = false
      this.handlePeriodicConfigChange()
    },
    getActionCardTitle(actionIndex) {
      return this.$t('wkt.admin.workflows.actions.card_title', { index: actionIndex + 1 })
    },
    getActionConditionCount(action) {
      return this.getActionConditions(action).length
    },
    getConditionValuePlaceholder(condition) {
      if (!condition) {
        return ''
      }

      if (this.usesSelectableConditionValues(condition) || this.isConditionDateField(condition) || this.isConditionTimeField(condition)) {
        return ''
      }

      if (this.isConditionMultiValue(condition)) {
        return '1,2,3'
      }

      if (condition.operator === 'CONTAINS') {
        return 'texto'
      }

      return this.isConditionNumberField(condition) ? '123' : ''
    },
    formatExecutionStatus(status) {
      switch (status) {
        case 1: return this.$t('wkt.admin.workflows.execution.status.ok')
        case 2: return this.$t('wkt.admin.workflows.execution.status.partial')
        case 3: return this.$t('wkt.admin.workflows.execution.status.error')
        default: return this.$t('wkt.admin.workflows.execution.status.pending')
      }
    },
    formatExecutionActionStatus(status) {
      switch (status) {
        case 1: return this.$t('wkt.admin.workflows.execution.status.ok')
        case 2: return this.$t('wkt.admin.workflows.execution.status.error')
        default: return this.$t('wkt.admin.workflows.execution.status.pending')
      }
    },
    createLocalId() {
      const current = this.nextLocalId
      this.nextLocalId -= 1
      return current
    },
    createEmptyCondition() {
      return {
        localId: this.createLocalId(),
        id: null,
        fieldName: '',
        operator: 'EQ',
        fieldValue: '',
      }
    },
    createEmptyAction() {
      const localId = this.createLocalId()
      const defaults = this.getActionDefaults('CREATE_TASK', this.workflowForm.eventType || this.getDefaultEventTypeValue())
      return {
        localId,
        id: localId,
        actionType: defaults.actionType,
        _lastAppliedActionType: defaults.actionType,
        configText: defaults.configText,
        recipientType: defaults.recipientType,
        recipientConfigText: defaults.recipientConfigText,
      }
    },
    createEmptyActionCondition(actionId) {
      return {
        localId: this.createLocalId(),
        id: null,
        workflowActionId: actionId,
        fieldName: '',
        operator: 'EQ',
        fieldValue: '',
      }
    },
    resetEditorPanels() {
      this.conditionEditorPanel = this.workflowForm.conditions.length ? 0 : null
      this.actionEditorPanel = this.workflowForm.actions.length ? 0 : null
    },
    searchTimeOut() {
      if (this.timer) {
        clearTimeout(this.timer)
        this.timer = null
      }
      this.timer = setTimeout(() => {
        this.options.page = 1
        this.getItems()
      }, 800)
    },
    refreshModule() {
      if (this.dialogOnly) {
        return
      }
      this.getItems()
    },
    getItems() {
      if (this.dialogOnly) {
        return
      }
      this.apiLoading = true
      const { page, itemsPerPage } = this.options

      api.getWorkflows((page ? page - 1 : 0) * itemsPerPage, itemsPerPage, this.search ? this.search : 'none',
        data => {
          this.apiLoading = false
          this.items = data.entities || []
          this.totalItems = data.total || 0
        },
        err => {
          this.apiLoading = false
          this.doShowAlert({ text: err.message || err, color: 'error', timeout: 3000 })
        })
    },
    onReveal() {
      if (this.dialogOnly) {
        return
      }
      this.getItems()
    },
    open(action, payload) {
      this.loadContext()

      if (action === 'CREATE') {
        this.openNewWorkflow()
        return
      }

      if (action === 'TEMPLATE') {
        this.openTemplateDialog()
        return
      }

      if (!payload) {
        return
      }

      if (action === 'DUPLICATE') {
        this.duplicateWorkflowById(payload)
        return
      }

      if (action === 'TOGGLE') {
        this.toggleWorkflowById(payload)
        return
      }

      this.openWorkflow({ id: payload })
    },
    openNewWorkflow() {
      const defaultEventType = this.getDefaultEventTypeValue()
      const defaultPeriodicConfig = this.getDefaultPeriodicConfig()
      this.workflowForm = {
        id: null,
        name: '',
        description: '',
        eventType: defaultEventType,
        eventSubtype: '',
        enabled: true,
        shared: true,
        conditions: [],
        actions: [this.createEmptyAction()],
        actionConditions: [],
      }
      this.periodicConfig = defaultPeriodicConfig
      this.applyEventDefaults(defaultEventType)
      this.executionLogs = []
      this.editorStep = '1'
      this.resetEditorPanels()
      this.detailDialog = true
    },
    openTemplateDialog() {
      if (!this.canWrite) {
        return
      }

      this.templateDialog = true
      this.loadWorkflowTemplates()
    },
    closeTemplateDialog() {
      this.templateDialog = false
      this.templatesLoading = false
      this.createFromTemplateLoading = null
    },
    loadWorkflowTemplates() {
      this.templatesLoading = true
      api.getWorkflowTemplates(
        data => {
          this.templatesLoading = false
          this.workflowTemplates = data || []
        },
        err => {
          this.templatesLoading = false
          this.workflowTemplates = []
          this.doShowAlert({ text: err.message || err, color: 'error', timeout: 3000 })
        })
    },
    createWorkflowFromTemplate(templateCode) {
      if (!this.canWrite || !templateCode) {
        return
      }

      this.createFromTemplateLoading = templateCode
      api.createWorkflowFromTemplate(templateCode,
        data => {
          this.createFromTemplateLoading = null
          this.templateDialog = false
          this.workflowForm = this.normalizeWorkflow(data)
          this.executionLogs = []
          this.editorStep = '1'
          this.resetEditorPanels()
          this.detailDialog = true
          this.$emit('refresh')
          this.doShowAlert({ text: this.$t('wkt.admin.workflows.messages.created_from_template'), color: 'success', timeout: 2500 })
          this.getItems()
        },
        err => {
          this.createFromTemplateLoading = null
          this.doShowAlert({ text: err.message || err, color: 'error', timeout: 3000 })
        })
    },
    openWorkflow(item) {
      if (!item || !item.id) {
        return
      }

      this.detailDialog = true
      this.detailLoading = true
      this.editorStep = '1'
      this.resetEditorPanels()

      api.getWorkflow(item.id,
        data => {
          this.detailLoading = false
          this.workflowForm = this.normalizeWorkflow(data)
          this.resetEditorPanels()
          this.loadExecutionLogs(item.id)
        },
        err => {
          this.detailLoading = false
          this.doShowAlert({ text: err.message || err, color: 'error', timeout: 3000 })
        })
    },
    duplicateWorkflowById(workflowId) {
      if (!this.canWrite || !workflowId) {
        return
      }

      this.duplicateLoading = true
      api.duplicateWorkflow(workflowId,
        data => {
          this.duplicateLoading = false
          this.workflowForm = this.normalizeWorkflow(data)
          this.executionLogs = []
          this.resetEditorPanels()
          this.detailDialog = true
          this.$emit('refresh')
          this.doShowAlert({ text: this.$t('wkt.admin.workflows.messages.duplicated'), color: 'success', timeout: 2500 })
          this.getItems()
        },
        err => {
          this.duplicateLoading = false
          this.doShowAlert({ text: err.message || err, color: 'error', timeout: 3000 })
        })
    },
    toggleWorkflowById(workflowId) {
      if (!this.canWrite || !workflowId) {
        return
      }

      this.toggleLoading = true
      api.getWorkflow(workflowId,
        data => {
          const workflow = data && data.workflow ? data.workflow : {}
          api.toggleWorkflow(workflowId, !(workflow.enabled === true),
            toggledData => {
              this.toggleLoading = false
              if (this.workflowForm.id === workflowId) {
                this.workflowForm = this.normalizeWorkflow(toggledData)
                this.loadExecutionLogs(workflowId)
              }
              this.$emit('refresh')
              this.doShowAlert({
                text: (toggledData && toggledData.workflow && toggledData.workflow.enabled)
                  ? this.$t('wkt.admin.workflows.messages.enabled')
                  : this.$t('wkt.admin.workflows.messages.disabled'),
                color: 'success',
                timeout: 2500,
              })
              this.getItems()
            },
            err => {
              this.toggleLoading = false
              this.doShowAlert({ text: err.message || err, color: 'error', timeout: 3000 })
            })
        },
        err => {
          this.toggleLoading = false
          this.doShowAlert({ text: err.message || err, color: 'error', timeout: 3000 })
        })
    },
    normalizeTemplatePlaceholders(value) {
      if (typeof value !== 'string' || !value) {
        return value
      }

      return value.replace(/\{\{([^{}]+)\}\}/g, '[[$1]]')
    },
    normalizeWorkflow(data) {
      const workflow = data && data.workflow ? data.workflow : {}
      const actions = (data && data.actions ? data.actions : []).map(action => Object.assign({}, action, {
        configText: this.normalizeTemplatePlaceholders(action.configText || ''),
        recipientConfigText: this.normalizeTemplatePlaceholders(action.recipientConfigText || ''),
        localId: action.id || this.createLocalId(),
        _lastAppliedActionType: action.actionType || 'CREATE_TASK',
      }))
      const actionConditions = (data && data.actionConditions ? data.actionConditions : []).map(condition => this.normalizeCondition(Object.assign({}, condition, {
        localId: condition.id || this.createLocalId(),
      })))

      this.periodicConfig = workflow.eventType === 'PERIODIC_EVENT'
        ? (this.parsePeriodicSubtype(workflow.eventSubtype) || this.getDefaultPeriodicConfig())
        : this.getDefaultPeriodicConfig()

      actions.forEach(action => this.ensureAdminRecipientConfig(action, workflow.eventType || ''))

      return {
        id: workflow.id || null,
        name: workflow.name || '',
        description: workflow.description || '',
        eventType: workflow.eventType || '',
        eventSubtype: workflow.eventSubtype || '',
        enabled: workflow.enabled !== false,
        shared: true,
        conditions: (workflow.eventType === 'PERIODIC_EVENT' ? [] : (data && data.conditions ? data.conditions : [])).map(condition => this.normalizeCondition(Object.assign({}, condition, {
          localId: condition.id || this.createLocalId(),
        }))),
        actions,
        actionConditions,
      }
    },
    loadExecutionLogs(workflowId) {
      if (!workflowId) {
        this.executionLogs = []
        return
      }

      api.getWorkflowExecutionLogs(workflowId,
        data => {
          this.executionLogs = data || []
        },
        () => {
          this.executionLogs = []
        })
    },
    buildExecutionResultSummary(execution) {
      if (!execution || !execution.actionLogs || !execution.actionLogs.length) {
        return null
      }

      return execution.actionLogs
        .map(actionLog => actionLog.resultText || actionLog.errorMessage || actionLog.actionType)
        .filter(Boolean)
        .join(' · ')
    },
    addCondition() {
      this.workflowForm.conditions.push(this.createEmptyCondition())
      this.conditionEditorPanel = this.workflowForm.conditions.length - 1
    },
    addConditionFromSuggestion(fieldName) {
      const condition = this.createEmptyCondition()
      condition.fieldName = fieldName
      this.workflowForm.conditions.push(condition)
      this.conditionEditorPanel = this.workflowForm.conditions.length - 1
    },
    removeCondition(index) {
      this.workflowForm.conditions.splice(index, 1)
      if (!this.workflowForm.conditions.length) {
        this.conditionEditorPanel = null
      } else if (this.conditionEditorPanel >= this.workflowForm.conditions.length) {
        this.conditionEditorPanel = this.workflowForm.conditions.length - 1
      }
    },
    moveCondition(index, delta) {
      this.workflowForm.conditions = this.moveItemInArray(this.workflowForm.conditions, index, delta)
    },
    addAction() {
      this.workflowForm.actions.push(this.createEmptyAction())
      this.actionEditorPanel = this.workflowForm.actions.length - 1
    },
    addActionByType(actionType) {
      const action = this.createEmptyAction()
      action.actionType = actionType || action.actionType
      action.configText = ''
      action.recipientType = ''
      action.recipientConfigText = ''
      this.applyDefaultsToAction(action)
      this.workflowForm.actions.push(action)
      this.actionEditorPanel = this.workflowForm.actions.length - 1
    },
    removeAction(index) {
      const action = this.workflowForm.actions[index]
      this.workflowForm.actions.splice(index, 1)
      this.workflowForm.actionConditions = this.workflowForm.actionConditions.filter(x => x.workflowActionId !== action.id)
      if (!this.workflowForm.actions.length) {
        this.actionEditorPanel = null
      } else if (this.actionEditorPanel >= this.workflowForm.actions.length) {
        this.actionEditorPanel = this.workflowForm.actions.length - 1
      }
    },
    duplicateAction(index) {
      const source = this.workflowForm.actions[index]
      if (!source) {
        return
      }

      const duplicatedLocalId = this.createLocalId()
      const duplicatedAction = Object.assign({}, source, {
        id: duplicatedLocalId,
        localId: duplicatedLocalId,
      })

      const sourceConditions = this.getActionConditions(source)
      const duplicatedConditions = sourceConditions.map(condition => Object.assign({}, condition, {
        id: null,
        localId: this.createLocalId(),
        workflowActionId: duplicatedAction.id,
      }))

      this.workflowForm.actions.splice(index + 1, 0, duplicatedAction)
      this.workflowForm.actionConditions = this.workflowForm.actionConditions.concat(duplicatedConditions)
      this.actionEditorPanel = index + 1
    },
    moveAction(index, delta) {
      this.workflowForm.actions = this.moveItemInArray(this.workflowForm.actions, index, delta)
    },
    getActionConditions(action) {
      return this.workflowForm.actionConditions.filter(x => x.workflowActionId === action.id)
    },
    addActionCondition(action) {
      this.workflowForm.actionConditions.push(this.createEmptyActionCondition(action.id))
    },
    removeActionCondition(action, conditionIndex) {
      const conditions = this.getActionConditions(action)
      const target = conditions[conditionIndex]
      this.workflowForm.actionConditions = this.workflowForm.actionConditions.filter(x => x.localId !== target.localId)
    },
    moveActionCondition(action, conditionIndex, delta) {
      const conditions = this.getActionConditions(action)
      const target = conditions[conditionIndex]
      const swapTarget = conditions[conditionIndex + delta]

      if (!target || !swapTarget) {
        return
      }

      const allConditions = [...this.workflowForm.actionConditions]
      const currentIndex = allConditions.findIndex(item => item.localId === target.localId)
      const swapIndex = allConditions.findIndex(item => item.localId === swapTarget.localId)

      if (currentIndex === -1 || swapIndex === -1) {
        return
      }

      const currentCondition = allConditions[currentIndex]
      allConditions[currentIndex] = allConditions[swapIndex]
      allConditions[swapIndex] = currentCondition
      this.workflowForm.actionConditions = allConditions
    },
    moveItemInArray(items, index, delta) {
      const targetIndex = index + delta
      if (targetIndex < 0 || targetIndex >= items.length) {
        return items
      }

      const nextItems = [...items]
      const currentItem = nextItems[index]
      nextItems[index] = nextItems[targetIndex]
      nextItems[targetIndex] = currentItem
      return nextItems
    },
    handleEventTypeChange(value) {
      if (value === 'PERIODIC_EVENT') {
        this.periodicConfig = this.parsePeriodicSubtype(this.workflowForm.eventSubtype) || this.getDefaultPeriodicConfig()
        this.syncPeriodicSubtype()
        this.workflowForm.conditions = []
        this.conditionEditorPanel = null
      } else if (this.workflowForm.eventSubtype && this.parsePeriodicSubtype(this.workflowForm.eventSubtype)) {
        this.workflowForm.eventSubtype = ''
        this.periodicConfig = this.getDefaultPeriodicConfig()
      }

      this.applyEventDefaults(value)
      this.workflowForm.conditions.forEach(condition => this.handleConditionFieldChange(condition))
      this.workflowForm.actionConditions.forEach(condition => this.handleConditionFieldChange(condition))
    },
    applyEventDefaults(eventType) {
      if (!eventType) {
        return
      }

      if (eventType === 'PERIODIC_EVENT') {
        this.syncPeriodicSubtype()
      }

      if (!this.workflowForm.name) {
        this.workflowForm.name = this.getDefaultWorkflowName(eventType)
      }

      if (!this.workflowForm.description) {
        this.workflowForm.description = this.getDefaultWorkflowDescription(eventType)
      }

      if (this.workflowForm.actions.length === 1) {
        const firstAction = this.workflowForm.actions[0]
        if (firstAction && !firstAction.configText && !firstAction.recipientConfigText) {
          this.applyDefaultsToAction(firstAction)
        }
      }

      this.workflowForm.actions.forEach(action => this.ensureAdminRecipientConfig(action, eventType))
    },
    handleActionTypeInput(action, selectedActionType) {
      if (!action) {
        return
      }

      const nextActionType = selectedActionType || action.actionType || 'CREATE_TASK'
      if (action._lastAppliedActionType === nextActionType) {
        action.actionType = nextActionType
        return
      }

      action.actionType = nextActionType
      action.configText = ''
      action.recipientType = ''
      action.recipientConfigText = ''
      this.applyDefaultsToAction(action)
      action._lastAppliedActionType = nextActionType
    },
    handleRecipientTypeChange(action) {
      if (!action) {
        return
      }

      if (this.isPeriodicEvent && (action.recipientType === 'SUBJECT' || action.recipientType === 'MANAGER')) {
        action.recipientType = 'EXPLICIT'
      }

      if (action.recipientType === 'EXPLICIT' && !action.recipientConfigText) {
        action.recipientConfigText = '{\n  "employeeIds": []\n}'
        return
      }

      if (action.recipientType === 'ADMIN') {
        this.updateAdminRecipientPermissionModule(action, this.getAdminRecipientPermissionModule(action))
        return
      }

      if (action.recipientType !== 'EXPLICIT' && action.recipientConfigText === '{\n  "employeeIds": []\n}') {
        action.recipientConfigText = ''
      } else if (action.recipientType !== 'EXPLICIT' && action.recipientType !== 'ADMIN') {
        action.recipientConfigText = ''
      }
    },
    applyDefaultsToAction(action) {
      if (!action) {
        return
      }

      const defaults = this.getActionDefaults(action.actionType, this.workflowForm.eventType)
      action.actionType = defaults.actionType
      if (!action.configText) {
        action.configText = defaults.configText
      }
      if (!action.recipientType) {
        action.recipientType = defaults.recipientType
      }
      if (!action.recipientConfigText) {
        action.recipientConfigText = defaults.recipientConfigText
      }
    },
    getDefaultWorkflowName(eventType) {
      return this.formatEventType(eventType)
    },
    getDefaultWorkflowDescription(eventType) {
      return this.$t('wkt.admin.workflows.summary.description_template', {
        event: this.formatEventType(eventType) || '--'
      })
    },
    getActionDefaults(actionType, eventType) {
      const defaultRecipient = eventType === 'PERIODIC_EVENT'
        ? 'EXPLICIT'
        : (eventType === 'SURVEY_SENT' ? 'ADMIN' : 'SUBJECT')
      const defaultAdminRecipientConfigText = `{\n  "permissionModule": "${this.getDefaultAdminRecipientPermissionModule(eventType)}"\n}`
      const defaultsByType = {
        CREATE_TASK: {
          actionType: 'CREATE_TASK',
          recipientType: eventType === 'OVERTIME_DETECTED' ? 'MANAGER' : defaultRecipient,
          recipientConfigText: defaultRecipient === 'ADMIN' ? defaultAdminRecipientConfigText : '',
          configText: '{\n  "title": "Revisar [[eventType]]",\n  "description": "Revisa la novedad asociada a [[eventType]]",\n  "dueDays": 2\n}',
        },
        SEND_EMAIL: {
          actionType: 'SEND_EMAIL',
          recipientType: defaultRecipient,
          recipientConfigText: defaultRecipient === 'ADMIN' ? defaultAdminRecipientConfigText : '',
          configText: '{\n  "subject": "Acción requerida: [[eventType]]",\n  "body": "Hola,\\n\\nSe ha producido el evento [[eventType]].\\n\\nReferencia: [[employeeId]]"\n}',
        },
        SEND_MESSAGE: {
          actionType: 'SEND_MESSAGE',
          recipientType: defaultRecipient,
          recipientConfigText: defaultRecipient === 'ADMIN' ? defaultAdminRecipientConfigText : '',
          configText: '{\n  "title": "Nueva gestión",\n  "body": "Revisa el evento [[eventType]]",\n  "sticky": false\n}',
        },
        REQUEST_DOCUMENT: {
          actionType: 'REQUEST_DOCUMENT',
          recipientType: eventType === 'PERIODIC_EVENT' ? 'EXPLICIT' : 'SUBJECT',
          recipientConfigText: eventType === 'PERIODIC_EVENT' ? '{\n  "employeeIds": []\n}' : '',
          configText: '{\n  "requestTitle": "Aporta documentación",\n  "requestMessage": "Necesitamos la documentación relacionada con [[eventType]]",\n  "showDocument": true,\n  "notifyEmployee": true\n}',
        },
        SHARE_DOCUMENT: {
          actionType: 'SHARE_DOCUMENT',
          recipientType: eventType === 'PERIODIC_EVENT' ? 'EXPLICIT' : 'SUBJECT',
          recipientConfigText: eventType === 'PERIODIC_EVENT' ? '{\n  "employeeIds": []\n}' : '',
          configText: '{\n  "sourceDocumentId": null,\n  "requestTitle": "Documento para revisar",\n  "requestMessage": "Consulta el documento relacionado con [[eventType]]",\n  "requestSignature": false,\n  "showDocument": true,\n  "notifyEmployee": true,\n  "signatureMode": "SMS"\n}',
        },
        SEND_FORM: {
          actionType: 'SEND_FORM',
          recipientType: eventType === 'PERIODIC_EVENT' ? 'EXPLICIT' : 'SUBJECT',
          recipientConfigText: eventType === 'PERIODIC_EVENT' ? '{\n  "employeeIds": []\n}' : '',
          configText: '{\n  "formId": null,\n  "requestTitle": "Completa el formulario",\n  "requestMessage": "Necesitamos tu respuesta para [[eventType]]",\n  "showDocument": true,\n  "notifyEmployee": true\n}',
        },
      }

      return defaultsByType[actionType] || defaultsByType.CREATE_TASK
    },
    getActionPresetItems(action) {
      const actionType = action && action.actionType ? action.actionType : null
      const presetsByType = {
        CREATE_TASK: [
          { code: 'recommended', label: this.$t('wkt.admin.workflows.presets.recommended') },
          { code: 'follow_up', label: this.$t('wkt.admin.workflows.presets.follow_up_task') },
          { code: 'review', label: this.$t('wkt.admin.workflows.presets.review_task') },
        ],
        SEND_EMAIL: [
          { code: 'recommended', label: this.$t('wkt.admin.workflows.presets.recommended') },
          { code: 'request_attention', label: this.$t('wkt.admin.workflows.presets.request_attention_email') },
          { code: 'informative', label: this.$t('wkt.admin.workflows.presets.informative_email') },
        ],
        SEND_MESSAGE: [
          { code: 'recommended', label: this.$t('wkt.admin.workflows.presets.recommended') },
          { code: 'short_message', label: this.$t('wkt.admin.workflows.presets.short_message') },
          { code: 'sticky_alert', label: this.$t('wkt.admin.workflows.presets.sticky_alert') },
        ],
        REQUEST_DOCUMENT: [
          { code: 'recommended', label: this.$t('wkt.admin.workflows.presets.recommended') },
          { code: 'document_required', label: this.$t('wkt.admin.workflows.presets.document_required') },
          { code: 'document_reminder', label: this.$t('wkt.admin.workflows.presets.document_reminder') },
        ],
        SHARE_DOCUMENT: [
          { code: 'recommended', label: this.$t('wkt.admin.workflows.presets.recommended') },
          { code: 'share_read_only', label: this.$t('wkt.admin.workflows.presets.share_read_only') },
          { code: 'share_with_signature', label: this.$t('wkt.admin.workflows.presets.share_with_signature') },
        ],
        SEND_FORM: [
          { code: 'recommended', label: this.$t('wkt.admin.workflows.presets.recommended') },
          { code: 'form_required', label: this.$t('wkt.admin.workflows.presets.form_required') },
          { code: 'form_reminder', label: this.$t('wkt.admin.workflows.presets.form_reminder') },
        ],
      }

      return presetsByType[actionType] || []
    },
    applyActionPreset(action, presetCode) {
      if (!action) {
        return
      }

      const eventType = this.workflowForm.eventType || 'EMPLOYEE_CREATED'
      const recommendedDefaults = this.getActionDefaults(action.actionType, eventType)
      const config = this.parseJsonSafely(recommendedDefaults.configText).value || {}
      const recipientType = recommendedDefaults.recipientType
      const recipientConfigText = recommendedDefaults.recipientConfigText || ''

      if (action.actionType === 'CREATE_TASK') {
        if (presetCode === 'follow_up') {
          config.title = `Seguimiento [[eventType]]`
          config.description = 'Haz seguimiento interno del evento [[eventType]] y documenta el resultado.'
          config.dueDays = 2
        } else if (presetCode === 'review') {
          config.title = `Revisar [[eventType]]`
          config.description = 'Revisa el contexto del evento [[eventType]] antes de continuar el proceso.'
          config.dueDays = 1
        }
      } else if (action.actionType === 'SEND_EMAIL') {
        if (presetCode === 'request_attention') {
          config.subject = 'Acción requerida: [[eventType]]'
          config.body = 'Hola,\n\nNecesitamos que revises el evento [[eventType]].\n\nReferencia: [[employeeId]]'
        } else if (presetCode === 'informative') {
          config.subject = 'Información sobre [[eventType]]'
          config.body = 'Hola,\n\nTe informamos de que se ha producido [[eventType]].\n\nGracias.'
        }
      } else if (action.actionType === 'SEND_MESSAGE') {
        if (presetCode === 'short_message') {
          config.title = 'Nueva gestión'
          config.body = 'Revisa [[eventType]]'
          config.sticky = false
        } else if (presetCode === 'sticky_alert') {
          config.title = 'Aviso importante'
          config.body = 'Tienes una acción pendiente relacionada con [[eventType]].'
          config.sticky = true
        }
      } else if (action.actionType === 'REQUEST_DOCUMENT') {
        if (presetCode === 'document_required') {
          config.requestTitle = 'Documentación obligatoria'
          config.requestMessage = 'Necesitamos la documentación asociada a [[eventType]] para continuar.'
          config.showDocument = true
          config.notifyEmployee = true
        } else if (presetCode === 'document_reminder') {
          config.requestTitle = 'Recordatorio de documentación'
          config.requestMessage = 'Sigue pendiente la documentación relacionada con [[eventType]].'
          config.showDocument = true
          config.notifyEmployee = true
        }
      } else if (action.actionType === 'SHARE_DOCUMENT') {
        if (presetCode === 'share_read_only') {
          config.requestTitle = 'Documento compartido'
          config.requestMessage = 'Tienes un documento nuevo relacionado con [[eventType]].'
          config.requestSignature = false
          config.showDocument = true
          config.notifyEmployee = true
          config.signatureMode = 'SMS'
        } else if (presetCode === 'share_with_signature') {
          config.requestTitle = 'Firma requerida'
          config.requestMessage = 'Necesitamos tu firma en el documento relacionado con [[eventType]].'
          config.requestSignature = true
          config.showDocument = true
          config.notifyEmployee = true
          config.signatureMode = 'SMS'
        }
      } else if (action.actionType === 'SEND_FORM') {
        if (presetCode === 'form_required') {
          config.requestTitle = 'Formulario obligatorio'
          config.requestMessage = 'Completa el formulario asociado a [[eventType]] para continuar.'
          config.showDocument = true
          config.notifyEmployee = true
        } else if (presetCode === 'form_reminder') {
          config.requestTitle = 'Recordatorio de formulario'
          config.requestMessage = 'Sigue pendiente el formulario asociado a [[eventType]].'
          config.showDocument = true
          config.notifyEmployee = true
        }
      }

      action.configText = this.serializeJsonObject(config)
      action.recipientType = action.recipientType || recipientType
      if (!action.recipientConfigText) {
        action.recipientConfigText = recipientConfigText
      }
      this.doShowAlert({
        text: this.$t('wkt.admin.workflows.messages.preset_applied'),
        color: 'success',
        timeout: 2200,
      })
    },
    isGuidedActionType(actionType) {
      return ['CREATE_TASK', 'SEND_EMAIL', 'SEND_MESSAGE', 'REQUEST_DOCUMENT', 'SHARE_DOCUMENT', 'SEND_FORM'].includes(actionType)
    },
    getParsedActionConfig(action) {
      const validation = this.parseJsonSafely(action && action.configText ? action.configText : '')
      if (!validation.valid || !validation.value || typeof validation.value !== 'object' || Array.isArray(validation.value)) {
        return {}
      }

      return validation.value
    },
    getActionConfigValue(action, key) {
      const config = this.getParsedActionConfig(action)
      return config[key] == null ? '' : config[key]
    },
    getActionConfigBooleanValue(action, key, fallbackValue = false) {
      const config = this.getParsedActionConfig(action)
      return typeof config[key] === 'boolean' ? config[key] : fallbackValue
    },
    updateActionConfigValue(action, key, value) {
      if (!action) {
        return
      }

      const config = this.getParsedActionConfig(action)
      config[key] = value
      action.configText = this.serializeJsonObject(config)
    },
    updateNumericActionConfigValue(action, key, value) {
      if (!action) {
        return
      }

      const config = this.getParsedActionConfig(action)
      if (value == null || value === '') {
        delete config[key]
      } else {
        const numericValue = Number(value)
        config[key] = Number.isNaN(numericValue) ? value : numericValue
      }
      action.configText = this.serializeJsonObject(config)
    },
    updateBooleanActionConfigValue(action, key, value) {
      if (!action) {
        return
      }

      const config = this.getParsedActionConfig(action)
      config[key] = value === true
      action.configText = this.serializeJsonObject(config)
    },
    serializeJsonObject(value) {
      return JSON.stringify(value || {}, null, 2)
    },
    getParsedRecipientConfig(action) {
      const validation = this.parseJsonSafely(action && action.recipientConfigText ? action.recipientConfigText : '')
      if (!validation.valid || !validation.value || typeof validation.value !== 'object' || Array.isArray(validation.value)) {
        return {}
      }

      return validation.value
    },
    getExplicitRecipientEmployeeIds(action) {
      const config = this.getParsedRecipientConfig(action)
      return Array.isArray(config.employeeIds) ? config.employeeIds : []
    },
    updateExplicitRecipientEmployeeIds(action, value) {
      if (!action) {
        return
      }

      let employeeIds = []

      if (Array.isArray(value)) {
        employeeIds = value
          .map(item => Number(item))
          .filter(item => !Number.isNaN(item))
      } else {
        employeeIds = String(value || '')
          .split(',')
          .map(item => item.trim())
          .filter(item => item !== '')
          .map(item => Number(item))
          .filter(item => !Number.isNaN(item))
      }

      action.recipientConfigText = this.serializeJsonObject({ employeeIds })
    },
    getActionRequiredConfigKeys(action) {
      const actionType = action && action.actionType ? action.actionType : null
      const keysByType = {
        CREATE_TASK: ['title', 'description'],
        SEND_EMAIL: ['subject', 'body'],
        SEND_MESSAGE: ['title', 'body'],
        REQUEST_DOCUMENT: ['requestTitle', 'requestMessage'],
        SHARE_DOCUMENT: ['sourceDocumentId', 'requestTitle'],
        SEND_FORM: ['formId', 'requestTitle'],
      }

      return keysByType[actionType] || []
    },
    getMissingActionConfigKeys(action) {
      const requiredKeys = this.getActionRequiredConfigKeys(action)
      if (!requiredKeys.length) {
        return []
      }

      const validation = this.parseJsonSafely(action && action.configText ? action.configText : '')
      if (!validation.valid || !validation.value || typeof validation.value !== 'object' || Array.isArray(validation.value)) {
        return requiredKeys
      }

      return requiredKeys.filter(key => validation.value[key] == null || validation.value[key] === '')
    },
    parseJsonSafely(text) {
      if (!text || !text.trim()) {
        return { valid: true, value: null }
      }

      try {
        return { valid: true, value: JSON.parse(text) }
      } catch (error) {
        return { valid: false, value: null }
      }
    },
    isConditionValuePresent(fieldValue) {
      if (Array.isArray(fieldValue)) {
        return fieldValue.some(item => String(item || '').trim() !== '')
      }

      return String(fieldValue == null ? '' : fieldValue).trim() !== ''
    },
    isConditionFilled(condition) {
      return !!(condition && (condition.fieldName || this.isConditionValuePresent(condition.fieldValue)))
    },
    isConditionComplete(condition) {
      return !!(condition && condition.fieldName && condition.operator && this.isConditionValuePresent(condition.fieldValue))
    },
    serializeConditionFieldValue(condition) {
      if (!condition) {
        return ''
      }

      const normalizedFieldName = this.normalizeConditionFieldName(condition.fieldName)
      const serializeSingleValue = value => {
        const normalizedValue = String(value == null ? '' : value).trim()
        if (!normalizedValue) {
          return ''
        }

        if (this.getConditionFieldDefinition(normalizedFieldName).type === 'date') {
          return this.normalizeDateValueForSave(normalizedValue)
        }

        return normalizedValue
      }

      if (Array.isArray(condition.fieldValue)) {
        return condition.fieldValue
          .map(item => serializeSingleValue(item))
          .filter(item => item !== '')
          .join(',')
      }

      return serializeSingleValue(condition.fieldValue)
    },
    isActionValidForStep(action) {
      if (!action || !action.actionType || !action.recipientType) {
        return false
      }

      if (this.isPeriodicEvent && ['SUBJECT', 'MANAGER'].includes(action.recipientType)) {
        return false
      }

      const configValidation = this.parseJsonSafely(action.configText)
      if (!configValidation.valid || this.getMissingActionConfigKeys(action).length) {
        return false
      }

      if (action.recipientType === 'EXPLICIT') {
        const recipientValidation = this.parseJsonSafely(action.recipientConfigText)
        return recipientValidation.valid
          && recipientValidation.value
          && Array.isArray(recipientValidation.value.employeeIds)
          && recipientValidation.value.employeeIds.length > 0
      }

      if (action.recipientType === 'ADMIN') {
        const recipientValidation = this.parseJsonSafely(action.recipientConfigText)
        return recipientValidation.valid
          && recipientValidation.value
          && typeof recipientValidation.value.permissionModule === 'string'
          && recipientValidation.value.permissionModule.trim() !== ''
      }

      const actionConditions = this.getActionConditions(action)
      return actionConditions.every(condition => !this.isConditionFilled(condition) || this.isConditionComplete(condition))
    },
    getWorkflowValidationState() {
      const errors = []
      const warnings = []

      if (!this.workflowForm.name) {
        errors.push(this.$t('wkt.admin.workflows.validation.name_required'))
      }

      if (!this.workflowForm.eventType) {
        errors.push(this.$t('wkt.admin.workflows.validation.event_required'))
      }

      if (this.isPeriodicEvent) {
        if (!this.periodicConfig.frequency) {
          errors.push(this.$t('wkt.admin.workflows.validation.periodic_frequency_required'))
        }

        if (this.periodicConfig.frequency === 'WEEKLY' && !this.periodicConfig.weekday) {
          errors.push(this.$t('wkt.admin.workflows.validation.periodic_weekday_required'))
        }

        if (this.periodicConfig.frequency === 'MONTHLY') {
          const dayOfMonth = Number(this.periodicConfig.dayOfMonth)
          if (Number.isNaN(dayOfMonth) || dayOfMonth < 1 || dayOfMonth > 31) {
            errors.push(this.$t('wkt.admin.workflows.validation.periodic_day_of_month_required'))
          }
        }

        if (!this.isValidPeriodicTime(this.periodicConfig.time)) {
          errors.push(this.$t('wkt.admin.workflows.validation.periodic_time_required'))
        }
      }

      if (!this.workflowForm.actions.length) {
        errors.push(this.$t('wkt.admin.workflows.validation.at_least_one_action'))
      }

      this.workflowForm.conditions.forEach((condition, index) => {
        if (this.isConditionFilled(condition) && !this.isConditionComplete(condition)) {
          warnings.push(this.$t('wkt.admin.workflows.validation.incomplete_condition', { index: index + 1 }))
        }
      })

      this.workflowForm.actions.forEach((action, index) => {
        if (!action.actionType) {
          errors.push(this.$t('wkt.admin.workflows.validation.action_type_required', { index: index + 1 }))
          return
        }

        if (!action.recipientType) {
          errors.push(this.$t('wkt.admin.workflows.validation.recipient_required', { index: index + 1 }))
        }

        if (this.isPeriodicEvent && ['SUBJECT', 'MANAGER'].includes(action.recipientType)) {
          errors.push(this.$t('wkt.admin.workflows.validation.periodic_unsupported_recipient', {
            index: index + 1,
          }))
        }

        const configValidation = this.parseJsonSafely(action.configText)
        if (!configValidation.valid) {
          errors.push(this.$t('wkt.admin.workflows.validation.invalid_action_config', { index: index + 1 }))
        } else {
          const missingKeys = this.getMissingActionConfigKeys(action)
          if (missingKeys.length) {
            errors.push(this.$t('wkt.admin.workflows.validation.missing_action_config_keys', {
              index: index + 1,
              keys: missingKeys.join(', '),
            }))
          }
        }

        if (action.recipientType === 'EXPLICIT') {
          const recipientValidation = this.parseJsonSafely(action.recipientConfigText)
          if (!recipientValidation.valid) {
            errors.push(this.$t('wkt.admin.workflows.validation.invalid_explicit_recipient', { index: index + 1 }))
          } else if (!recipientValidation.value || !Array.isArray(recipientValidation.value.employeeIds) || !recipientValidation.value.employeeIds.length) {
            errors.push(this.$t('wkt.admin.workflows.validation.explicit_recipient_shape', { index: index + 1 }))
          }
        } else if (action.recipientType === 'ADMIN') {
          const recipientValidation = this.parseJsonSafely(action.recipientConfigText)
          if (!recipientValidation.valid || !recipientValidation.value || typeof recipientValidation.value.permissionModule !== 'string'
            || recipientValidation.value.permissionModule.trim() === '') {
            errors.push(this.$t('wkt.admin.workflows.validation.admin_permission_module_required', { index: index + 1 }))
          }
        }

        const actionConditions = this.getActionConditions(action)
        actionConditions.forEach((condition, conditionIndex) => {
          if (this.isConditionFilled(condition) && !this.isConditionComplete(condition)) {
            warnings.push(this.$t('wkt.admin.workflows.validation.incomplete_action_condition', {
              action: index + 1,
              condition: conditionIndex + 1,
            }))
          }
        })
      })

      return { errors, warnings }
    },
    saveWorkflow() {
      if (!this.canWrite) {
        return
      }

      if (this.isPeriodicEvent) {
        this.syncPeriodicSubtype()
      }

      if (!this.$refs.workflowFormRef || !this.$refs.workflowFormRef.validate()) {
        return
      }

      if (this.validationState.errors.length) {
        this.doShowAlert({ text: this.$t('wkt.admin.workflows.validation.fix_errors'), color: 'error', timeout: 3500 })
        return
      }

      this.saveLoading = true
      const payload = {
        workflow: {
          id: this.workflowForm.id,
          name: this.workflowForm.name,
          description: this.workflowForm.description,
          eventType: this.workflowForm.eventType,
          eventSubtype: this.workflowForm.eventSubtype,
          enabled: this.workflowForm.enabled,
          shared: true,
        },
        conditions: (this.isPeriodicEvent ? [] : this.workflowForm.conditions)
          .filter(condition => this.isConditionComplete(condition))
          .map((condition, index) => ({
            id: condition.id,
            fieldName: this.normalizeConditionFieldName(condition.fieldName),
            operator: condition.operator,
            fieldValue: this.serializeConditionFieldValue(condition),
            sortOrder: index,
          })),
        actions: this.workflowForm.actions
          .filter(x => x.actionType)
          .map((action, index) => ({
            id: action.id,
            actionType: action.actionType,
            configText: this.normalizeTemplatePlaceholders(action.configText || ''),
            recipientType: action.recipientType || 'SUBJECT',
            recipientConfigText: this.normalizeTemplatePlaceholders(action.recipientConfigText || ''),
            sortOrder: index,
          })),
        actionConditions: this.workflowForm.actionConditions
          .filter(condition => this.isConditionComplete(condition))
          .map((condition, index) => ({
            id: condition.id,
            workflowActionId: condition.workflowActionId,
            fieldName: this.normalizeConditionFieldName(condition.fieldName),
            operator: condition.operator,
            fieldValue: this.serializeConditionFieldValue(condition),
            sortOrder: index,
          })),
      }

      api.storeWorkflow(payload,
        data => {
          this.saveLoading = false
          this.workflowForm = this.normalizeWorkflow(data)
          this.loadExecutionLogs(this.workflowForm.id)
          this.$emit('refresh')
          this.doShowAlert({ text: this.$t('wkt.admin.workflows.messages.saved'), color: 'success', timeout: 2500 })
          this.getItems()
          this.closeDetailDialog()
        },
        err => {
          this.saveLoading = false
          this.doShowAlert({ text: err.message || err, color: 'error', timeout: 3000 })
        })
    },
    deleteSelectedWorkflow() {
      if (!this.canWrite || !this.workflowForm.id) {
        return
      }

      if (this.$refs.deleteWorkflowConfirmDialog) {
        this.$refs.deleteWorkflowConfirmDialog.confirm(this.workflowForm.id)
      }
    },
    confirmDeleteSelectedWorkflow() {
      if (!this.canWrite || !this.workflowForm.id) {
        return
      }

      this.deleteLoading = true
      api.deleteWorkflow(this.workflowForm.id,
        () => {
          this.deleteLoading = false
          this.detailDialog = false
          this.$emit('refresh')
          this.doShowAlert({ text: this.$t('wkt.admin.workflows.messages.deleted'), color: 'success', timeout: 2500 })
          this.getItems()
        },
        err => {
          this.deleteLoading = false
          this.doShowAlert({ text: err.message || err, color: 'error', timeout: 3000 })
        })
    },
    duplicateSelectedWorkflow() {
      if (!this.canWrite || !this.workflowForm.id) {
        return
      }
      this.duplicateWorkflowById(this.workflowForm.id)
    },
    toggleSelectedWorkflow() {
      if (!this.canWrite || !this.workflowForm.id) {
        return
      }

      if (this.workflowForm.enabled) {
        if (this.$refs.disableWorkflowConfirmDialog) {
          this.$refs.disableWorkflowConfirmDialog.confirm(this.workflowForm.id)
        }
        return
      }

      this.confirmToggleSelectedWorkflow()
    },
    confirmToggleSelectedWorkflow() {
      if (!this.canWrite || !this.workflowForm.id) {
        return
      }

      this.toggleLoading = true
      api.toggleWorkflow(this.workflowForm.id, !this.workflowForm.enabled,
        data => {
          this.toggleLoading = false
          this.workflowForm = this.normalizeWorkflow(data)
          this.$emit('refresh')
          this.doShowAlert({
            text: this.workflowForm.enabled
              ? this.$t('wkt.admin.workflows.messages.enabled')
              : this.$t('wkt.admin.workflows.messages.disabled'),
            color: 'success',
            timeout: 2500,
          })
          this.getItems()
        },
        err => {
          this.toggleLoading = false
          this.doShowAlert({ text: err.message || err, color: 'error', timeout: 3000 })
        })
    },
    closeDetailDialog() {
      this.detailDialog = false
      this.detailLoading = false
      this.editorStep = '1'
      this.workflowForm = {
        id: null,
        name: '',
        description: '',
        eventType: '',
        eventSubtype: '',
        enabled: true,
        shared: true,
        conditions: [],
        actions: [],
        actionConditions: [],
      }
      this.executionLogs = []
      this.periodicConfig = this.getDefaultPeriodicConfig()
      this.duplicateLoading = false
      this.deleteLoading = false
      this.toggleLoading = false
      this.conditionEditorPanel = null
      this.actionEditorPanel = 0
      this.workflowTemplates = []
      this.templateDialog = false
      this.templatesLoading = false
      this.createFromTemplateLoading = null
    },
  },
  watch: {
    options: {
      handler () {
        if (this.dialogOnly) {
          return
        }
        this.getItems()
      },
      deep: true,
    },
  },
}
</script>

<style scoped>
.workflow-row {
  cursor: pointer;
}

.workflow-editor-tabs {
  border-bottom: 1px solid #e0e0e0;
}

::v-deep .workflow-editor-tabs .v-tab {
  text-transform: none;
  letter-spacing: normal;
  min-width: 0;
}

::v-deep .workflow-editor-tab-items {
  background: transparent;
}

.workflow-clickable-chip {
  cursor: pointer;
}

.workflow-action-zone {
  height: 100%;
}

::v-deep .workflow-dialog-menu {
  z-index: 260 !important;
  overflow-y: auto !important;
  overscroll-behavior: contain;
}

::v-deep .workflow-dialog-menu .v-list {
  overscroll-behavior: contain;
}
</style>
